import React, { useState, useMemo } from 'react';
import NodeEntityModal from '../graph/node/NodeEntityModal';
import SynapseEntityModal from '../graph/synapses/SynapseEntityModal';
import TextWithTables from '../common/TextWithTables';
import ActivationGraph from '../explainability/ActivationGraph';
import './ExecutionInsights.css';

const FATE_STAGE_ORDER = ['seed_selection', 'bm25_filter', 'propagation', 'snn_firing', 'potency'];
const FATE_STAGE_LABELS = {
  seed_selection: 'Seed Selection (Pinecone)',
  bm25_filter: 'BM25 Keyword Filter',
  propagation: 'Evidence Propagation',
  snn_firing: 'Engine Simulation',
  potency: 'Potency Scoring',
};

/**
 * ExecutionInsightDetail - Unified detail view for a single pipeline execution.
 *
 * Sections:
 * 1.  Summary bar
 * 1b. Query Expansion
 * 2.  Rate Coding Derivation (hybrid params + per-node breakdown)
 * 3.  Unified Fired Nodes table (merged activations + rate_coding + potency)
 * 4.  Nodes Below Potency Threshold (excluded nodes analysis)
 * 5.  Explanation Chain / Propagation visual
 * 6.  Chain Analysis
 * 6b. Prioritized Context Bundle
 * 6c. Platform Context Assembly (Technical Payload)
 * 7.  LLM Prompt section (system message, full prompt, Platform summary, diagnostics)
 * 8.  Strongest Neurons
 * 9.  Synapse Relationships
 * 10. Gate Evaluation
 * 11. Fired Per Tick
 * 12. Full Answer
 */
function ExecutionInsightDetail({ insight, graphId }) {
  const trace = insight.execution_trace || {};
  const nodeActivations = trace.node_activations || [];
  const rateCoding = [...(trace.rate_coding || [])].sort((a, b) => (b.rate_hz || 0) - (a.rate_hz || 0));
  const synapses = trace.synapses || [];
  const explanationChain = trace.explanation_chain || [];
  const firedPerTick = trace.fired_per_tick || [];
  const gates = trace.gates || [];
  const rateCodingParams = trace.rate_coding_params || null;
  const propagationStats = trace.propagation_stats || null;
  const chainAnalysis = trace.chain_analysis || null;
  const queryExpansion = trace.query_expansion || null;
  const potencyScores = trace.potency_scores || {};
  const contextAssembly = trace.context_assembly || null;
  const candidateFates = trace.candidate_fates || [];
  const promptExpansions = insight.prompt_expansions || {};
  const mlcfTrace = trace.mlcf_trace || null;
  const crsTrace = trace.crs_trace || null;
  const nodeMlcfMetadata = trace.node_mlcf_metadata || {};
  const phaseTimings = trace.phase_timings || null;
  const llmUsage = insight.llm_usage || null;
  const ragSavings = insight.rag_savings || null;
  // SEM (Spiking Episodic Memory) summary -- attached by the GraphBuilder run
  // into metadata.sem, persisted in AnswerMetadata (falls back to the trace).
  const sem = insight.answer_metadata?.sem || trace.sem || null;

  // Modal state
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [synapseModalOpen, setSynapseModalOpen] = useState(false);
  const [selectedSynapseId, setSelectedSynapseId] = useState(null);

  // Trace search
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const executeSearch = () => setSearchTerm(searchInput.trim());
  const clearSearch = () => { setSearchInput(''); setSearchTerm(''); };

  // Collapsible sections
  const [showSynapseTable, setShowSynapseTable] = useState(false);
  const [showFiredPerTick, setShowFiredPerTick] = useState(false);
  const [showRateCoding, setShowRateCoding] = useState(false);
  const [showAllRateCoding, setShowAllRateCoding] = useState(false);
  const [expandedNodeId, setExpandedNodeId] = useState(null);
  const [showLlmPrompt, setShowLlmPrompt] = useState(false);
  const [showJudgePrompt, setShowJudgePrompt] = useState(false);
  const [showExcludedNodes, setShowExcludedNodes] = useState(false);
  const [showContextAssembly, setShowContextAssembly] = useState(false);
  const [showAssemblyExcluded, setShowAssemblyExcluded] = useState(false);
  const [showFateTracker, setShowFateTracker] = useState(false);
  const [showSem, setShowSem] = useState(false);

  // Fate tracker search
  const [fateSearchInput, setFateSearchInput] = useState('');
  const [fateSearchTerm, setFateSearchTerm] = useState('');
  const executeFateSearch = () => setFateSearchTerm(fateSearchInput.trim());
  const clearFateSearch = () => { setFateSearchInput(''); setFateSearchTerm(''); };

  // Unified nodes table state
  const [sortColumn, setSortColumn] = useState('potency');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAllUnifiedNodes, setShowAllUnifiedNodes] = useState(false);
  const [filterExcluded, setFilterExcluded] = useState(true); // show all by default including excluded

  // ---- Build unified nodes data ----
  const unifiedNodes = useMemo(() => {
    // Build lookup maps
    const activationMap = {};
    nodeActivations.forEach(a => { activationMap[a.node_id] = a; });
    const rateCodingMap = {};
    rateCoding.forEach(rc => { rateCodingMap[rc.node_id] = rc; });

    // Start with all unique node IDs from all sources
    const allNodeIds = new Set();
    nodeActivations.forEach(a => allNodeIds.add(a.node_id));
    rateCoding.forEach(rc => allNodeIds.add(rc.node_id));
    Object.keys(potencyScores).forEach(id => allNodeIds.add(id));

    return Array.from(allNodeIds).map(nodeId => {
      const act = activationMap[nodeId] || {};
      const rc = rateCodingMap[nodeId] || {};
      const pot = potencyScores[nodeId] || {};

      return {
        node_id: nodeId,
        label: act.label || rc.label || pot.node_name || nodeId?.substring(0, 16) + '...',
        node_type: pot.node_type || 'unknown',
        potency: pot.potency ?? null,
        tier: pot.tier || (act.spike_count > 0 ? 'EVIDENCE' : 'UNKNOWN'),
        similarity: rc.similarity ?? pot.query_relevance ?? null,
        rate_hz: rc.rate_hz ?? pot.rate_hz ?? act.mean_rate_hz ?? null,
        threshold: act.threshold ?? null,
        spike_count: act.spike_count ?? pot.spike_count ?? 0,
        evidence: act.evidence ?? pot.evidence ?? rc.evidence ?? null,
        hop: rc.hop ?? pot.hop ?? null,
        is_seed: act.is_seed || (!rc.is_propagated && rc.node_id != null),
        is_outcome: act.is_outcome || false,
        is_verbatim: pot.is_verbatim || false,
        in_llm_context: pot.tier === 'EVIDENCE' || pot.tier === 'VERBATIM_REFERENCE',
        // For expandable detail
        neuron_model: act.neuron_model,
        tau_m: act.tau_m,
        firing_equation: act.firing_equation,
        peak_rate_hz: act.peak_rate_hz,
        bm25_raw: rc.bm25_raw,
        hybrid_score: rc.hybrid_score,
        activation_path: rc.activation_path || pot.activation_path || [],
        contributing_synapses: rc.contributing_synapses || [],
        gate_contributions: pot.gate_contributions || [],
        gate_bonus: pot.gate_bonus,
        query_relevance: pot.query_relevance,
        norm_rate: pot.norm_rate,
        norm_evidence: pot.norm_evidence,
        norm_spikes: pot.norm_spikes,
        prop_score: pot.prop_score,
      };
    });
  }, [nodeActivations, rateCoding, potencyScores]);

  // Filter and sort unified nodes
  const sortedUnifiedNodes = useMemo(() => {
    let filtered = filterExcluded
      ? unifiedNodes
      : unifiedNodes.filter(n => n.tier !== 'EXCLUDED');

    return [...filtered].sort((a, b) => {
      const aVal = a[sortColumn] ?? -Infinity;
      const bVal = b[sortColumn] ?? -Infinity;
      if (sortDirection === 'desc') return bVal - aVal;
      return aVal - bVal;
    });
  }, [unifiedNodes, sortColumn, sortDirection, filterExcluded]);

  // Excluded nodes for analysis section
  const excludedNodes = useMemo(() => {
    return unifiedNodes
      .filter(n => n.tier === 'EXCLUDED')
      .sort((a, b) => (b.potency ?? 0) - (a.potency ?? 0));
  }, [unifiedNodes]);

  // Aggregate synapses by relation type
  const relationCounts = {};
  synapses.forEach(syn => {
    const rel = syn.relation || syn.edge_name || 'unknown';
    relationCounts[rel] = (relationCounts[rel] || 0) + 1;
  });
  const sortedRelations = Object.entries(relationCounts).sort((a, b) => b[1] - a[1]);

  // Strongest neurons
  const strongestByRate = [...nodeActivations].sort((a, b) => (b.peak_rate_hz || 0) - (a.peak_rate_hz || 0)).slice(0, 10);

  // ---- Handlers ----

  const openNodeModal = (nodeId) => {
    setSelectedNodeId(nodeId);
    setNodeModalOpen(true);
  };

  const openSynapseModal = (synapseId) => {
    setSelectedSynapseId(synapseId);
    setSynapseModalOpen(true);
  };

  const toggleNodeExpand = (nodeId) => {
    setExpandedNodeId(prev => prev === nodeId ? null : nodeId);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <span className="sort-icon sort-inactive">&udarr;</span>;
    return <span className="sort-icon sort-active">{sortDirection === 'desc' ? '\u25BC' : '\u25B2'}</span>;
  };

  // Helper: generate actionable suggestion for excluded node
  const getExcludedSuggestion = (node) => {
    const suggestions = [];
    const threshold = potencyScores[Object.keys(potencyScores).find(k =>
      potencyScores[k].tier === 'EVIDENCE'
    )]?.potency || 0.10;

    if ((node.similarity ?? 0) < 0.15) {
      suggestions.push({
        component: 'Similarity',
        value: node.similarity?.toFixed(3) || '0.000',
        action: 'Edit node description or NlRawText to include terms matching typical queries for this topic.',
      });
    }
    if ((node.rate_hz ?? 0) < 10) {
      suggestions.push({
        component: 'Measure',
        value: node.rate_hz?.toFixed(1) || '0.0',
        action: 'Increase semantic relevance or add stronger connections from seed nodes.',
      });
    }
    if ((node.evidence ?? 0) < 0.05) {
      suggestions.push({
        component: 'Evidence',
        value: node.evidence?.toFixed(3) || '0.000',
        action: 'Ensure the node receives input from multiple active connections to accumulate evidence.',
      });
    }
    if ((node.spike_count ?? 0) < 1) {
      suggestions.push({
        component: 'Selection Count',
        value: String(node.spike_count ?? 0),
        action: 'Lower the node threshold (theta) or increase input mass to enable selection.',
      });
    }
    if ((node.gate_bonus ?? 0) === 0) {
      suggestions.push({
        component: 'Gate Contribution',
        value: '0',
        action: 'Add this node as an input to a relevant gate to receive the gate bonus.',
      });
    }
    if ((node.hop ?? 0) > 2) {
      suggestions.push({
        component: 'Propagation Depth',
        value: `Hop ${node.hop}`,
        action: 'Create a direct connection from a seed node to reduce hop distance.',
      });
    }
    if (suggestions.length === 0) {
      suggestions.push({
        component: 'Overall',
        value: node.potency?.toFixed(4) || '0.0000',
        action: 'Node is close to the threshold. Strengthening any signal component may push it above.',
      });
    }
    return suggestions;
  };

  // Build synapse lookup for propagation chain
  const synapseBySourceTarget = useMemo(() => {
    const map = {};
    synapses.forEach(syn => {
      const key = `${syn.source_id}->${syn.target_id}`;
      map[key] = syn;
    });
    return map;
  }, [synapses]);

  // ---- Node fate tracker ----
  const groupedFates = useMemo(() => {
    if (!candidateFates.length) return null;
    const groups = {};
    FATE_STAGE_ORDER.forEach(s => { groups[s] = { included: [], excluded: [] }; });
    candidateFates.forEach(f => {
      const stage = f.stage || 'unknown';
      if (!groups[stage]) groups[stage] = { included: [], excluded: [] };
      if (f.status === 'included') groups[stage].included.push(f);
      else groups[stage].excluded.push(f);
    });
    return groups;
  }, [candidateFates]);

  const fateSummary = useMemo(() => {
    if (!candidateFates.length) return null;
    const totalExcluded = candidateFates.filter(f => f.status === 'excluded').length;
    const totalIncluded = candidateFates.filter(f => f.status === 'included').length;
    return { totalExcluded, totalIncluded, total: candidateFates.length };
  }, [candidateFates]);

  const filteredGroupedFates = useMemo(() => {
    if (!groupedFates) return null;
    const q = fateSearchTerm.toLowerCase();
    if (!q) return groupedFates;
    const filtered = {};
    Object.keys(groupedFates).forEach(stage => {
      const g = groupedFates[stage];
      const matchFate = (f) =>
        (f.label && f.label.toLowerCase().includes(q)) ||
        (f.node_id && f.node_id.toLowerCase().includes(q)) ||
        (f.detail && f.detail.toLowerCase().includes(q));
      filtered[stage] = {
        included: g.included.filter(matchFate),
        excluded: g.excluded.filter(matchFate),
      };
    });
    return filtered;
  }, [groupedFates, fateSearchTerm]);

  const filteredFateSummary = useMemo(() => {
    if (!filteredGroupedFates) return null;
    let totalIncluded = 0, totalExcluded = 0;
    Object.values(filteredGroupedFates).forEach(g => {
      totalIncluded += g.included.length;
      totalExcluded += g.excluded.length;
    });
    return { totalIncluded, totalExcluded, total: totalIncluded + totalExcluded };
  }, [filteredGroupedFates]);

  // ---- Trace search ----
  const searchResults = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return null;

    const matches = (str) => str && str.toLowerCase().includes(q);

    const nodeMatches = unifiedNodes.filter(n =>
      matches(n.label) || matches(n.node_id) || matches(n.node_type) || matches(n.tier)
    );

    const synapseMatches = synapses.filter(s =>
      matches(s.source_label) || matches(s.target_label) ||
      matches(s.relation) || matches(s.edge_name) || matches(s.synapse_id)
    );

    const gateMatches = gates.filter(g =>
      matches(g.gate_id) || matches(g.gate_type) || matches(g.rule_text)
    );

    const chainMatches = explanationChain.filter(h =>
      matches(h.source_label) || matches(h.source_node_id)
    );

    const nodeLabelById = {};
    unifiedNodes.forEach(n => { nodeLabelById[n.node_id] = n.label; });

    const tickMatches = [];
    firedPerTick.forEach((tick, idx) => {
      const nodeIds = Array.isArray(tick) ? tick : (tick.fired_node_ids || tick.node_ids || []);
      const matched = nodeIds.filter(nid => matches(nid) || matches(nodeLabelById[nid]));
      if (matched.length > 0) {
        tickMatches.push({ tick: idx + 1, matchedIds: matched, totalFired: nodeIds.length });
      }
    });

    const assemblyEvidenceMatches = (contextAssembly?.evidence_nodes || []).filter(n =>
      matches(n.label) || matches(n.node_id)
    );
    const assemblyVerbatimMatches = (contextAssembly?.verbatim_nodes || []).filter(n =>
      matches(n.label) || matches(n.node_id)
    );

    const fateMatches = candidateFates.filter(f =>
      matches(f.label) || matches(f.node_id) || matches(f.detail) || matches(f.stage)
    );

    const total = nodeMatches.length + synapseMatches.length + gateMatches.length +
      chainMatches.length + tickMatches.length + assemblyEvidenceMatches.length +
      assemblyVerbatimMatches.length + fateMatches.length;

    return {
      total,
      nodes: nodeMatches,
      synapses: synapseMatches,
      gates: gateMatches,
      chain: chainMatches,
      ticks: tickMatches,
      assemblyEvidence: assemblyEvidenceMatches,
      assemblyVerbatim: assemblyVerbatimMatches,
      fates: fateMatches,
      nodeLabelById,
    };
  }, [searchTerm, unifiedNodes, synapses, gates, explanationChain, firedPerTick, contextAssembly, candidateFates]);

  const highlightMatch = (text) => {
    if (!text || !searchTerm.trim()) return text;
    const q = searchTerm.trim();
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.substring(0, idx)}
        <mark className="search-highlight">{text.substring(idx, idx + q.length)}</mark>
        {text.substring(idx + q.length)}
      </>
    );
  };

  return (
    <div className="insight-detail">
      {/* 1. Summary Bar */}
      <div className="insight-summary-bar">
        {trace.total_response_time_ms != null && (
          <div className="summary-item">
            <span className="summary-label">Response Time</span>
            <span className="summary-value">
              {trace.total_response_time_ms >= 1000
                ? (trace.total_response_time_ms / 1000).toFixed(1) + 's'
                : trace.total_response_time_ms.toFixed(0) + 'ms'}
            </span>
          </div>
        )}
        {/* Engine Total: sum of all pipeline.run_compiled passes (including
            the auto-vocabulary requery when it fires). When this differs
            from total_pipeline the user can immediately see that a retry
            ran. When it differs significantly from Response Time, the gap
            lives in the route handler (classifier, expansion, linked
            processes, trace assembly, SSE serialization). */}
        {phaseTimings && phaseTimings.engine_total != null
          && phaseTimings.engine_total !== phaseTimings.total_pipeline && (
          <div className="summary-item">
            <span className="summary-label">
              Engine Total
              {phaseTimings.engine_total_runs > 1
                ? ` (${phaseTimings.engine_total_runs} runs)` : ''}
            </span>
            <span className="summary-value">
              {phaseTimings.engine_total >= 1000
                ? (phaseTimings.engine_total / 1000).toFixed(1) + 's'
                : phaseTimings.engine_total.toFixed(0) + 'ms'}
            </span>
          </div>
        )}
        {/* Route Overhead: response_time - engine_total. The portion of
            wall-clock time spent OUTSIDE the engine (HTTP, classifier,
            vocabulary expansion, linked-process resolution, trace build,
            SSE serialization). Surfaces the true cost of the route
            wrapper alongside the engine cost. */}
        {phaseTimings && trace.total_response_time_ms != null && (() => {
          const eng = phaseTimings.engine_total != null
            ? phaseTimings.engine_total
            : phaseTimings.total_pipeline;
          if (eng == null) return null;
          const overhead = trace.total_response_time_ms - eng;
          if (overhead < 50) return null;
          return (
            <div className="summary-item">
              <span className="summary-label">Route Overhead</span>
              <span className="summary-value">
                {overhead >= 1000
                  ? (overhead / 1000).toFixed(1) + 's'
                  : overhead.toFixed(0) + 'ms'}
              </span>
            </div>
          );
        })()}
        {phaseTimings && !!phaseTimings.auto_vocabulary_requery && (
          <div className="summary-item" title="No firing on first pass; pipeline ran a second time with vocabulary expansion">
            <span className="summary-label">Auto Requery</span>
            <span className="summary-value">on</span>
          </div>
        )}
        {insight.score != null && (
          <div className="summary-item">
            <span className="summary-label">Score</span>
            <span className="summary-value">{(insight.score * 100).toFixed(1)}%</span>
          </div>
        )}
        {insight.duration_ms != null && (
          <div className="summary-item">
            <span className="summary-label">Duration</span>
            <span className="summary-value">
              {insight.duration_ms >= 1000
                ? (insight.duration_ms / 1000).toFixed(1) + 's'
                : insight.duration_ms.toFixed(0) + 'ms'}
            </span>
          </div>
        )}
        {insight.total_ticks != null && (
          <div className="summary-item">
            <span className="summary-label">Ticks</span>
            <span className="summary-value">{insight.total_ticks}</span>
          </div>
        )}
        {insight.total_spikes != null && (
          <div className="summary-item">
            <span className="summary-label">Total Selections</span>
            <span className="summary-value">{insight.total_spikes}</span>
          </div>
        )}
        {insight.llm_model && (
          <div className="summary-item">
            <span className="summary-label">LLM</span>
            <span className="summary-value">{insight.llm_model}</span>
          </div>
        )}
        {insight.snn_execution_mode && (
          <div className="summary-item">
            <span className="summary-label">Platform Mode</span>
            <span className="summary-value">{insight.snn_execution_mode}</span>
          </div>
        )}
        {insight.insight_id && (
          <div className="summary-item">
            <span className="summary-label">Execution ID</span>
            <span className="summary-value summary-value-mono">{insight.insight_id}</span>
          </div>
        )}
      </div>

      {/* Pipeline Performance */}
      {phaseTimings && (() => {
        const PHASE_LABELS = {
          phase1_intent_scope: 'Intent & Scope',
          phase1_intent: 'Intent & Scope',
          phase2_candidate_selection: 'Candidate Selection',
          phase2_candidates: 'Candidate Selection',
          phase2b_mlcf: 'MLCF Seed Filter',
          phase3_compile_snn_page: 'Compile Engine Page',
          phase4_rate_coding: 'Relevance Scoring',
          phase4b_crs: 'CRS Overlay',
          phase5_effective_configs: 'Effective Configs',
          phase5_hydrate: 'Hydrate Subgraph',
          phase5_5_synapse_relevance: 'Connection Relevance',
          phase6_snn_simulation: 'Engine Simulation',
          phase6_snn: 'Engine Simulation',
          phase7_readout: 'Readout',
          phase8_render_answer: 'Render Answer',
          phase8_render: 'Render Answer',
          phase9_judge: 'Judge LLM',
          // Route-level non-engine envelopes (stamped by the API handler).
          // Together with the engine phases they sum to ``route_total``
          // (i.e. wall-clock time inside the route handler). The bar chart
          // uses ``route_total`` as the 100% denominator when these are
          // present, so engine phases visually shrink to their true share
          // of wall-clock time and the route envelopes show the gap.
          // ``route_pipeline`` is intentionally NOT added here because it
          // overlaps the engine phases; we keep it as a TOTAL_KEY so it
          // never renders as a duplicate segment.
          route_pre_pipeline: 'Route: Pre-Pipeline',
          route_post_pipeline: 'Route: Post-Pipeline',
          route_requery: 'Route: Auto Requery',
        };
        const PHASE_COLORS = [
          '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
          '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
          '#af7aa1', '#86bcb6',
        ];
        // Totals / metadata that must never render as bar segments.
        const TOTAL_KEYS = ['total_pipeline', 'total', 'engine_total', 'route_total',
          'route_pipeline', 'engine_total_runs', 'auto_vocabulary_requery',
          'total_pipeline_first_run', 'total_pipeline_requery',
          'phase8_network_rtt_ms', 'phase8_ttft_ms', 'phase8_prefill_ms',
          'phase8_decode_ms', 'phase8_total_ms', 'phase8_token_count', 'phase8_tokens_per_sec'];
        const PHASE_ORDER = [
          'route_pre_pipeline',
          'phase1_intent_scope', 'phase1_intent',
          'phase2_candidate_selection', 'phase2_candidates',
          'phase2b_mlcf',
          'phase3_compile_snn_page',
          'phase4_rate_coding',
          'phase4b_crs',
          'phase5_effective_configs', 'phase5_hydrate',
          'phase5_5_synapse_relevance',
          'phase6_snn_simulation', 'phase6_snn',
          'phase7_readout',
          'phase8_render_answer', 'phase8_render',
          'phase9_judge',
          'route_requery',
          'route_post_pipeline',
        ];

        const phases = Object.entries(phaseTimings)
          .filter(([k]) => !TOTAL_KEYS.includes(k) && PHASE_LABELS[k])
          .map(([k, v]) => ({ key: k, label: PHASE_LABELS[k], ms: v }))
          .filter(p => typeof p.ms === 'number' && p.ms > 0)
          .sort((a, b) => {
            const ia = PHASE_ORDER.indexOf(a.key);
            const ib = PHASE_ORDER.indexOf(b.key);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
          });

        // Choose the denominator that matches what's in the chart. When
        // route envelopes are present, prefer ``route_total`` (so engine
        // and route segments together sum to 100%); else fall back to the
        // engine total like before.
        const hasRouteSegments = phases.some(p => p.key.startsWith('route_'));
        const totalMs = hasRouteSegments && phaseTimings.route_total != null
          ? phaseTimings.route_total
          : (phaseTimings.total_pipeline != null
              ? phaseTimings.total_pipeline
              : phases.reduce((s, p) => s + p.ms, 0));

        if (phases.length === 0) return null;

        return (
          <div className="insight-section pipeline-performance-section">
            <h3 className="insight-section-title">Pipeline Performance</h3>

            <div className="pipeline-bar-chart" title={`Total: ${totalMs.toFixed(0)}ms`}>
              {phases.map((p, i) => {
                const pct = totalMs > 0 ? (p.ms / totalMs) * 100 : 0;
                if (pct < 0.5) return null;
                return (
                  <div
                    key={p.key}
                    className="pipeline-bar-segment"
                    style={{ width: pct + '%', backgroundColor: PHASE_COLORS[i % PHASE_COLORS.length] }}
                    title={`${p.label}: ${p.ms.toFixed(0)}ms (${pct.toFixed(1)}%)`}
                  >
                    {pct > 8 && <span className="pipeline-bar-label">{p.label}</span>}
                  </div>
                );
              })}
            </div>

            <table className="insight-table compact pipeline-timing-table">
              <thead>
                <tr>
                  <th style={{width: 14}}></th>
                  <th>Phase</th>
                  <th style={{textAlign:'right'}}>Duration</th>
                  <th style={{textAlign:'right'}}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {phases.map((p, i) => {
                  const pct = totalMs > 0 ? (p.ms / totalMs) * 100 : 0;
                  return (
                    <tr key={p.key}>
                      <td>
                        <span
                          className="pipeline-color-swatch"
                          style={{ backgroundColor: PHASE_COLORS[i % PHASE_COLORS.length] }}
                        />
                      </td>
                      <td>{p.label}</td>
                      <td style={{textAlign:'right', fontVariantNumeric:'tabular-nums'}}>
                        {p.ms >= 1000 ? (p.ms / 1000).toFixed(2) + 's' : p.ms.toFixed(0) + 'ms'}
                      </td>
                      <td style={{textAlign:'right', fontVariantNumeric:'tabular-nums'}}>
                        {pct.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="pipeline-total-row">
                  <td></td>
                  <td><strong>{hasRouteSegments ? 'Total (wall-clock)' : 'Total'}</strong></td>
                  <td style={{textAlign:'right', fontVariantNumeric:'tabular-nums'}}>
                    <strong>{totalMs >= 1000 ? (totalMs / 1000).toFixed(2) + 's' : totalMs.toFixed(0) + 'ms'}</strong>
                  </td>
                  <td style={{textAlign:'right'}}><strong>100%</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })()}

      {/* LLM Streaming Metrics (network, TTFT, throughput) */}
      {phaseTimings && phaseTimings.phase8_ttft_ms != null && (() => {
        const fmt = (ms) => ms >= 1000 ? (ms / 1000).toFixed(2) + 's' : ms.toFixed(0) + 'ms';
        return (
          <div className="insight-section">
            <h3 className="insight-section-title">LLM Streaming Breakdown</h3>
            <table className="insight-table compact">
              <tbody>
                {phaseTimings.phase8_network_rtt_ms != null && (
                  <tr><td>Network RTT (tunnel)</td><td>{fmt(phaseTimings.phase8_network_rtt_ms)}</td></tr>
                )}
                <tr><td>Time to First Token</td><td>{fmt(phaseTimings.phase8_ttft_ms)}</td></tr>
                {phaseTimings.phase8_prefill_ms != null && (
                  <tr><td>Prefill (est.)</td><td>{fmt(phaseTimings.phase8_prefill_ms)}</td></tr>
                )}
                {phaseTimings.phase8_decode_ms != null && (
                  <tr><td>Decode (generation)</td><td>{fmt(phaseTimings.phase8_decode_ms)}</td></tr>
                )}
                {phaseTimings.phase8_token_count != null && (
                  <tr><td>Tokens Generated</td><td>{phaseTimings.phase8_token_count}</td></tr>
                )}
                {phaseTimings.phase8_tokens_per_sec != null && (
                  <tr><td>Throughput</td><td>{phaseTimings.phase8_tokens_per_sec} tok/s</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Token Consumption */}
      {llmUsage && (llmUsage.overall || llmUsage.prompt_tokens != null || llmUsage.total_tokens != null) && (() => {
        // Support both the legacy aggregator shape ({overall: {...}, per_phase, per_kind})
        // and the new flat LLMUsageInfo shape (top-level prompt_tokens, completion_tokens, ...).
        const ov = llmUsage.overall || {
          prompt_tokens: llmUsage.prompt_tokens,
          completion_tokens: llmUsage.completion_tokens,
          total_tokens: llmUsage.total_tokens,
          cost_usd: llmUsage.cost_usd,
          call_count: llmUsage.call_count,
          model: llmUsage.model_name,
          latency_ms: llmUsage.response_time_ms,
        };
        const perPhase = llmUsage.per_phase || {};
        const perKind = llmUsage.per_kind || {};
        const calls = Array.isArray(llmUsage.calls) ? llmUsage.calls : [];
        const fmtNum = (n) => Number(n || 0).toLocaleString();
        const fmtCost = (c) => {
          const v = Number(c || 0);
          if (v < 0.0001 && v > 0) return '<$0.0001';
          if (v < 0.01) return `$${v.toFixed(5)}`;
          return `$${v.toFixed(4)}`;
        };
        const fmtMs = (ms) => {
          if (!ms) return '—';
          return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms.toFixed(0)}ms`;
        };
        const phaseRows = Object.values(perPhase || {})
          .map(p => ({ ...p, _sort: p.total_tokens || 0 }))
          .sort((a, b) => b._sort - a._sort);
        return (
          <div className="insight-section">
            <h3 className="insight-section-title">Token Consumption</h3>
            <div className="insight-summary-grid">
              <div className="summary-item">
                <span className="summary-label">Prompt Tokens</span>
                <span className="summary-value">{fmtNum(ov.prompt_tokens)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Completion Tokens</span>
                <span className="summary-value">{fmtNum(ov.completion_tokens)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Tokens</span>
                <span className="summary-value"><strong>{fmtNum(ov.total_tokens)}</strong></span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Estimated Cost</span>
                <span className="summary-value"><strong>{fmtCost(ov.cost_usd)}</strong></span>
              </div>
              <div className="summary-item">
                <span className="summary-label">LLM Calls</span>
                <span className="summary-value">{fmtNum(ov.call_count)}</span>
              </div>
              {ov.model && (
                <div className="summary-item">
                  <span className="summary-label">Primary Model</span>
                  <span className="summary-value summary-value-mono">{ov.model}</span>
                </div>
              )}
              {ov.latency_ms != null && (
                <div className="summary-item">
                  <span className="summary-label">LLM Latency</span>
                  <span className="summary-value">{fmtMs(ov.latency_ms)}</span>
                </div>
              )}
            </div>

            {/* Prompt Efficiency (Fix E telemetry) -- razor-sharp context dashboard */}
            {(llmUsage.prompt_chars != null
              || llmUsage.answer_chars != null
              || llmUsage.bundle_efficiency_ratio != null
              || llmUsage.prompt_chars_saved != null) && (
              <div style={{marginTop: '14px'}}>
                <div style={{
                  fontSize: '0.85em',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--text-secondary, #777)',
                  marginBottom: '6px',
                }}>
                  Prompt Efficiency (razor-sharp context)
                </div>
                <div className="insight-summary-grid">
                  {llmUsage.prompt_chars != null && (
                    <div className="summary-item">
                      <span className="summary-label">Prompt Chars</span>
                      <span className="summary-value">{fmtNum(llmUsage.prompt_chars)}</span>
                    </div>
                  )}
                  {llmUsage.answer_chars != null && (
                    <div className="summary-item">
                      <span className="summary-label">Answer Chars</span>
                      <span className="summary-value">{fmtNum(llmUsage.answer_chars)}</span>
                    </div>
                  )}
                  {llmUsage.bundle_chars != null && (
                    <div className="summary-item">
                      <span className="summary-label">Evidence Bundle</span>
                      <span className="summary-value">{fmtNum(llmUsage.bundle_chars)} chars</span>
                    </div>
                  )}
                  {llmUsage.bundle_efficiency_ratio != null && (() => {
                    const pct = (Number(llmUsage.bundle_efficiency_ratio) * 100).toFixed(1);
                    const eff = Number(llmUsage.bundle_efficiency_ratio);
                    let color = '#999';
                    if (eff >= 0.20) color = '#19a974';
                    else if (eff >= 0.10) color = '#f1a216';
                    else color = '#d9534f';
                    return (
                      <div className="summary-item">
                        <span className="summary-label">Answer / Prompt</span>
                        <span className="summary-value" style={{color}}>
                          <strong>{pct}%</strong>
                        </span>
                      </div>
                    );
                  })()}
                  {llmUsage.prompt_chars_saved != null && Number(llmUsage.prompt_chars_saved) > 0 && (
                    <div className="summary-item">
                      <span className="summary-label">Chars Saved (pruning)</span>
                      <span className="summary-value" style={{color: '#19a974'}}>
                        −{fmtNum(llmUsage.prompt_chars_saved)}
                      </span>
                    </div>
                  )}
                  {llmUsage.pre_pruning_prompt_chars_estimate != null && llmUsage.prompt_chars != null && (
                    <div className="summary-item">
                      <span className="summary-label">Without Pruning (est.)</span>
                      <span className="summary-value" style={{textDecoration: 'line-through', color: '#888'}}>
                        {fmtNum(llmUsage.pre_pruning_prompt_chars_estimate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {Object.keys(perKind || {}).length > 1 && (
              <div className="insight-table-scroll" style={{marginTop: '12px'}}>
                <table className="insight-table compact">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th style={{textAlign:'right'}}>Calls</th>
                      <th style={{textAlign:'right'}}>Prompt</th>
                      <th style={{textAlign:'right'}}>Completion</th>
                      <th style={{textAlign:'right'}}>Total</th>
                      <th style={{textAlign:'right'}}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(perKind).map((k, idx) => (
                      <tr key={`kind-${k.kind || idx}`}>
                        <td>{k.kind || '—'}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(k.call_count)}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(k.prompt_tokens)}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(k.completion_tokens)}</td>
                        <td style={{textAlign:'right'}}><strong>{fmtNum(k.total_tokens)}</strong></td>
                        <td style={{textAlign:'right'}}>{fmtCost(k.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {phaseRows.length > 0 && (
              <div className="insight-table-scroll" style={{marginTop: '12px'}}>
                <table className="insight-table compact">
                  <thead>
                    <tr>
                      <th>Phase</th>
                      <th>Model</th>
                      <th style={{textAlign:'right'}}>Calls</th>
                      <th style={{textAlign:'right'}}>Prompt</th>
                      <th style={{textAlign:'right'}}>Completion</th>
                      <th style={{textAlign:'right'}}>Total</th>
                      <th style={{textAlign:'right'}}>Cost</th>
                      <th style={{textAlign:'right'}}>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phaseRows.map((p) => (
                      <tr key={`phase-${p.phase}`}>
                        <td>{p.phase || '—'}</td>
                        <td className="summary-value-mono" style={{fontSize: '0.85em'}}>{p.model || '—'}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(p.call_count)}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(p.prompt_tokens)}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(p.completion_tokens)}</td>
                        <td style={{textAlign:'right'}}><strong>{fmtNum(p.total_tokens)}</strong></td>
                        <td style={{textAlign:'right'}}>{fmtCost(p.cost_usd)}</td>
                        <td style={{textAlign:'right'}}>{fmtMs(p.latency_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {calls.length > 0 && (
              <details style={{marginTop: '12px'}}>
                <summary style={{cursor: 'pointer', userSelect: 'none'}}>
                  <strong>{calls.length} individual LLM call{calls.length !== 1 ? 's' : ''}</strong>
                </summary>
                <div className="insight-table-scroll" style={{marginTop: '8px'}}>
                  <table className="insight-table compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Phase</th>
                        <th>Kind</th>
                        <th>Model</th>
                        <th style={{textAlign:'right'}}>Prompt</th>
                        <th style={{textAlign:'right'}}>Completion</th>
                        <th style={{textAlign:'right'}}>Total</th>
                        <th style={{textAlign:'right'}}>Cost</th>
                        <th style={{textAlign:'right'}}>Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((c, i) => (
                        <tr key={`call-${i}`}>
                          <td>{i + 1}</td>
                          <td>{c.phase || '—'}</td>
                          <td>{c.kind || 'chat'}</td>
                          <td className="summary-value-mono" style={{fontSize: '0.85em'}}>{c.model || '—'}</td>
                          <td style={{textAlign:'right'}}>{fmtNum(c.prompt_tokens)}</td>
                          <td style={{textAlign:'right'}}>{fmtNum(c.completion_tokens)}</td>
                          <td style={{textAlign:'right'}}>{fmtNum(c.total_tokens)}</td>
                          <td style={{textAlign:'right'}}>{fmtCost(c.cost_usd)}</td>
                          <td style={{textAlign:'right'}}>{fmtMs(c.latency_ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        );
      })()}

      {/* Activation Graph Visualization */}
      {unifiedNodes.length > 0 && (
        <div className="insight-section">
          <ActivationGraph
            seedNodes={unifiedNodes.filter(n => n.is_seed).map(n => ({
              node_id: n.node_id,
              node_name: n.name || n.label || '',
              similarity: n.potency || n.final_score || 0.8,
            }))}
            firedNodes={unifiedNodes.map(n => ({
              node_id: n.node_id,
              node_name: n.name || n.label || '',
              node_type: n.node_type || 'concept',
              potency: n.potency || n.final_score || 0.5,
              spike_count: n.spike_count || n.spikes || 1,
            }))}
            synapses={synapses.map(s => ({
              source_id: s.source_id || s.sourceId,
              target_id: s.target_id || s.targetId,
              weight: s.weight || 0.5,
              relevance: s.relevance || s.semantic_relevance || 0.3,
              relation: s.relation || s.edge_name || '',
            }))}
            onNodeClick={(nid) => {
              setSelectedNodeId(nid);
              setNodeModalOpen(true);
            }}
          />
        </div>
      )}

      {/* vs Traditional RAG */}
      {ragSavings && ragSavings.tiers && (() => {
        const tiers = ragSavings.tiers || {};
        const headline = ragSavings.headline || {};
        const actual = ragSavings.actual || {};
        const fmtNum = (n) => Number(n || 0).toLocaleString();
        const fmtCost = (c) => {
          const v = Number(c || 0);
          if (v < 0.0001 && v > 0) return '<$0.0001';
          if (v < 0.01) return `$${v.toFixed(5)}`;
          return `$${v.toFixed(4)}`;
        };
        const fmtPct = (p) => `${Number(p || 0).toFixed(1)}%`;
        const tierMeta = [
          { key: 'conservative', label: 'Conservative', tip: 'k=15 chunks, single LLM pass' },
          { key: 'realistic', label: 'Realistic', tip: 'k=25 chunks, single LLM pass' },
          { key: 'quality_matched', label: 'Quality-Matched (Headline)', tip: 'k=25 with re-ranking + multi-pass (1.5x)' },
          { key: 'naive_full_doc', label: 'Full-Document', tip: 'Entire document per query (compliance-safe RAG)' },
        ];
        return (
          <div className="insight-section">
            <h3 className="insight-section-title">vs Traditional RAG</h3>
            <div
              className="insight-summary-grid"
              style={{marginBottom: '8px'}}
              title="Headline savings compare actual usage to the Quality-Matched RAG tier."
            >
              <div className="summary-item">
                <span className="summary-label">Tokens Saved vs Quality-Matched RAG</span>
                <span className="summary-value">
                  <strong>{fmtPct(headline.savings_pct)}</strong>
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Cost Saved</span>
                <span className="summary-value"><strong>{fmtCost(headline.savings_cost_usd)}</strong></span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Actual Tokens</span>
                <span className="summary-value">{fmtNum(actual.total_tokens)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Actual Cost</span>
                <span className="summary-value">{fmtCost(actual.cost_usd)}</span>
              </div>
              {actual.basis && (
                <div className="summary-item">
                  <span className="summary-label">Basis</span>
                  <span className="summary-value">{actual.basis === 'measured' ? 'Measured' : 'Estimated'}</span>
                </div>
              )}
            </div>
            <div className="insight-table-scroll">
              <table className="insight-table compact">
                <thead>
                  <tr>
                    <th>RAG Tier</th>
                    <th>Description</th>
                    <th style={{textAlign:'right'}}>RAG Tokens</th>
                    <th style={{textAlign:'right'}}>RAG Cost</th>
                    <th style={{textAlign:'right'}}>Tokens Saved</th>
                    <th style={{textAlign:'right'}}>% Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {tierMeta.map(({ key, label, tip }) => {
                    const t = tiers[key];
                    if (!t) return null;
                    return (
                      <tr key={key} title={tip}>
                        <td>
                          <strong>{label}</strong>
                        </td>
                        <td style={{fontSize: '0.85em', color: '#666'}}>{t.description || tip}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(t.rag_only_total_tokens || t.total_tokens)}</td>
                        <td style={{textAlign:'right'}}>{fmtCost(t.cost_usd)}</td>
                        <td style={{textAlign:'right'}}>{fmtNum(t.savings_tokens_vs_actual)}</td>
                        <td style={{textAlign:'right'}}><strong>{fmtPct(t.savings_pct_vs_actual)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p style={{fontSize: '0.8em', color: '#888', marginTop: '6px'}}>
              Estimated against gpt-4o pricing ($2.50 / 1M input, $10 / 1M output). Tiers mirror the offline analysis.
            </p>
          </div>
        );
      })()}

      {/* Trace Search */}
      <div className="trace-search-bar">
        <input
          type="text"
          placeholder="Search trace (e.g. node name, connection, gate...)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') executeSearch(); }}
          className="trace-search-input"
        />
        <button className="trace-search-btn" onClick={executeSearch} title="Search">
          Search
        </button>
        {searchTerm && searchResults && (
          <span className="trace-search-count">
            {searchResults.total} result{searchResults.total !== 1 ? 's' : ''}
          </span>
        )}
        {searchTerm && (
          <button className="trace-search-clear" onClick={clearSearch} title="Clear search">
            &times;
          </button>
        )}
      </div>

      {searchResults && searchResults.total > 0 && (
        <div className="trace-search-results">
          {/* Node matches */}
          {searchResults.nodes.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Nodes ({searchResults.nodes.length})
              </h5>
              <div className="search-result-items">
                {searchResults.nodes.map((node) => (
                  <div key={node.node_id} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                      {highlightMatch(node.label)}
                    </button>
                    <span className="search-result-meta">
                      <span className={`badge badge-tier-${(node.tier || 'unknown').toLowerCase()}`}>
                        {node.tier === 'VERBATIM_REFERENCE' ? 'Verbatim' : node.tier || '?'}
                      </span>
                      {node.potency != null && (
                        <span className="search-meta-chip">potency={node.potency.toFixed(3)}</span>
                      )}
                      {node.rate_hz != null && (
                        <span className="search-meta-chip">rate={node.rate_hz.toFixed(1)}</span>
                      )}
                      {node.spike_count > 0 && (
                        <span className="search-meta-chip">selections={node.spike_count}</span>
                      )}
                      {node.evidence != null && (
                        <span className="search-meta-chip">evidence={node.evidence.toFixed(3)}</span>
                      )}
                      {node.in_llm_context
                        ? <span className="badge badge-fired">In LLM</span>
                        : <span className="badge badge-silent">Not in LLM</span>
                      }
                      {node.is_seed && <span className="badge badge-seed">Seed</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Synapse matches */}
          {searchResults.synapses.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Connections ({searchResults.synapses.length})
              </h5>
              <div className="search-result-items">
                {searchResults.synapses.map((syn, idx) => (
                  <div key={syn.synapse_id || idx} className="search-result-item">
                    <span className="search-synapse-chain">
                      <button className="entity-link node-link" onClick={() => openNodeModal(syn.source_id)}>
                        {highlightMatch(syn.source_label || syn.source_id?.substring(0, 12))}
                      </button>
                      <span className="search-synapse-arrow">&rarr;</span>
                      <button className="entity-link synapse-link" onClick={() => openSynapseModal(syn.synapse_id)}>
                        {highlightMatch(syn.relation || syn.edge_name || '-')}
                      </button>
                      <span className="search-synapse-arrow">&rarr;</span>
                      <button className="entity-link node-link" onClick={() => openNodeModal(syn.target_id)}>
                        {highlightMatch(syn.target_label || syn.target_id?.substring(0, 12))}
                      </button>
                    </span>
                    <span className="search-result-meta">
                      {syn.weight != null && <span className="search-meta-chip">w={syn.weight.toFixed(2)}</span>}
                      {syn.relevance != null && <span className="search-meta-chip">rel={syn.relevance.toFixed(3)}</span>}
                      {syn.confidence != null && <span className="search-meta-chip">conf={syn.confidence.toFixed(3)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gate matches */}
          {searchResults.gates.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Gates ({searchResults.gates.length})
              </h5>
              <div className="search-result-items">
                {searchResults.gates.map((gate, idx) => (
                  <div key={gate.gate_id || idx} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(gate.gate_id)}>
                      {highlightMatch(gate.gate_id?.substring(0, 16))}
                    </button>
                    <span className="search-result-meta">
                      <span className={`badge badge-gate-${(gate.gate_type || 'AND').toLowerCase()}`}>
                        {gate.gate_type || 'AND'}
                      </span>
                      {gate.fired
                        ? <span className="badge badge-fired">Satisfied</span>
                        : <span className="badge badge-silent">Pending</span>
                      }
                      {gate.rule_text && <span className="search-meta-chip">{gate.rule_text}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanation chain matches */}
          {searchResults.chain.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Explanation Chain ({searchResults.chain.length})
              </h5>
              <div className="search-result-items">
                {searchResults.chain.map((hop, idx) => (
                  <div key={idx} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(hop.source_node_id)}>
                      {highlightMatch(hop.source_label)}
                    </button>
                    <span className="search-result-meta">
                      <span className="search-meta-chip">{hop.spike_count} selections</span>
                      {hop.contribution != null && (
                        <span className="search-meta-chip">contrib={hop.contribution.toFixed(2)}</span>
                      )}
                      {hop.relation && <span className="search-meta-chip">{hop.relation}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fired per tick matches */}
          {searchResults.ticks.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Selected Per Tick ({searchResults.ticks.length} tick{searchResults.ticks.length !== 1 ? 's' : ''})
              </h5>
              <div className="search-result-items">
                {searchResults.ticks.map((t) => (
                  <div key={t.tick} className="search-result-item">
                    <span className="search-tick-label">T{t.tick}</span>
                    <span className="search-result-meta">
                      <span className="search-meta-chip">
                        {t.matchedIds.length} match{t.matchedIds.length !== 1 ? 'es' : ''} / {t.totalFired} selected
                      </span>
                      {t.matchedIds.slice(0, 3).map((nid, i) => (
                        <button key={i} className="entity-link node-link" onClick={() => openNodeModal(nid)}>
                          {highlightMatch(searchResults.nodeLabelById[nid] || nid.substring(0, 8))}
                        </button>
                      ))}
                      {t.matchedIds.length > 3 && <span className="search-meta-chip">+{t.matchedIds.length - 3} more</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Context assembly matches */}
          {(searchResults.assemblyEvidence.length > 0 || searchResults.assemblyVerbatim.length > 0) && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Context Assembly ({searchResults.assemblyEvidence.length + searchResults.assemblyVerbatim.length})
              </h5>
              <div className="search-result-items">
                {searchResults.assemblyEvidence.map((node, idx) => (
                  <div key={node.node_id || idx} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                      {highlightMatch(node.label || node.node_id?.substring(0, 16))}
                    </button>
                    <span className="search-result-meta">
                      <span className="badge badge-evidence">Evidence</span>
                      {node.potency != null && <span className="search-meta-chip">potency={node.potency.toFixed(4)}</span>}
                    </span>
                  </div>
                ))}
                {searchResults.assemblyVerbatim.map((node, idx) => (
                  <div key={node.node_id || idx} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                      {highlightMatch(node.label || node.node_id?.substring(0, 16))}
                    </button>
                    <span className="search-result-meta">
                      <span className="badge badge-verbatim_reference">Verbatim</span>
                      {node.potency != null && <span className="search-meta-chip">potency={node.potency.toFixed(4)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Fate matches in search */}
          {searchResults.fates && searchResults.fates.length > 0 && (
            <div className="search-result-group">
              <h5 className="search-result-group-title">
                Node Fates ({searchResults.fates.length})
              </h5>
              <div className="search-result-items">
                {searchResults.fates.map((fate, idx) => (
                  <div key={`${fate.node_id}-${fate.stage}-${idx}`} className="search-result-item">
                    <button className="entity-link node-link" onClick={() => openNodeModal(fate.node_id)}>
                      {highlightMatch(fate.label)}
                    </button>
                    <span className="search-result-meta">
                      <span className={`badge badge-fate-${fate.status}`}>{fate.status}</span>
                      <span className="search-meta-chip">{FATE_STAGE_LABELS[fate.stage] || fate.stage}</span>
                      {fate.detail && <span className="search-meta-chip">{fate.detail}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {searchResults && searchResults.total === 0 && (
        <div className="trace-search-no-results">
          No matches found for &ldquo;{searchTerm.trim()}&rdquo; in this trace.
        </div>
      )}

      {/* Node Fate Tracker */}
      {candidateFates.length > 0 && groupedFates && (
        <div className="insight-section fate-tracker-section">
          <h4 className="insight-section-title">
            Node Fate Tracker
            {fateSummary && (
              <span className="fate-summary-badge">
                {fateSummary.totalIncluded} included, {fateSummary.totalExcluded} excluded
              </span>
            )}
            <button className="btn-link btn-inline-toggle" onClick={() => setShowFateTracker(!showFateTracker)}>
              {showFateTracker ? 'Hide' : 'Show'}
            </button>
          </h4>
          <p className="section-hint">
            Tracks every candidate node through the 5 pipeline stages. Shows where and why nodes were included or excluded.
          </p>
          {showFateTracker && (
            <>
            <div className="fate-search-bar">
              <input
                type="text"
                placeholder="Search fates (node name, ID, detail...)"
                value={fateSearchInput}
                onChange={(e) => setFateSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') executeFateSearch(); }}
                className="trace-search-input"
              />
              <button className="trace-search-btn" onClick={executeFateSearch}>Search</button>
              {fateSearchTerm && filteredFateSummary && (
                <span className="trace-search-count">
                  {filteredFateSummary.total} result{filteredFateSummary.total !== 1 ? 's' : ''}
                </span>
              )}
              {fateSearchTerm && (
                <button className="trace-search-clear" onClick={clearFateSearch}>&times;</button>
              )}
            </div>
            <div className="fate-stages">
              {FATE_STAGE_ORDER.map(stage => {
                const group = filteredGroupedFates ? filteredGroupedFates[stage] : groupedFates[stage];
                if (!group || (group.included.length === 0 && group.excluded.length === 0)) return null;
                return (
                  <div key={stage} className="fate-stage-group">
                    <h5 className="fate-stage-title">
                      {FATE_STAGE_LABELS[stage] || stage}
                      <span className="fate-stage-counts">
                        <span className="fate-count-included">{group.included.length} included</span>
                        <span className="fate-count-excluded">{group.excluded.length} excluded</span>
                      </span>
                    </h5>
                    {group.excluded.length > 0 && (
                      <div className="fate-items">
                        {group.excluded.slice(0, 30).map((fate, idx) => (
                          <div key={`${fate.node_id}-${idx}`} className="fate-item fate-excluded">
                            <button className="entity-link node-link" onClick={() => openNodeModal(fate.node_id)}>
                              {fate.label}
                            </button>
                            <span className="fate-detail">{fate.detail}</span>
                            <span className="fate-metrics">
                              {fate.similarity != null && <span className="fate-metric">sim={fate.similarity.toFixed(3)}</span>}
                              {fate.bm25_raw != null && <span className="fate-metric">bm25={fate.bm25_raw.toFixed(4)}</span>}
                              {fate.rate_hz != null && <span className="fate-metric">rate={fate.rate_hz.toFixed(1)}</span>}
                              {fate.evidence != null && <span className="fate-metric">evidence={fate.evidence.toFixed(4)}</span>}
                              {fate.hop != null && <span className="fate-metric">hop={fate.hop}</span>}
                            </span>
                          </div>
                        ))}
                        {group.excluded.length > 30 && (
                          <div className="more-indicator">...and {group.excluded.length - 30} more excluded</div>
                        )}
                      </div>
                    )}
                    {group.included.length > 0 && (
                      <div className="fate-items">
                        {group.included.slice(0, 15).map((fate, idx) => (
                          <div key={`${fate.node_id}-${idx}`} className="fate-item fate-included">
                            <button className="entity-link node-link" onClick={() => openNodeModal(fate.node_id)}>
                              {fate.label}
                            </button>
                            <span className="fate-detail">{fate.detail}</span>
                            <span className="fate-metrics">
                              {fate.similarity != null && <span className="fate-metric">sim={fate.similarity.toFixed(3)}</span>}
                              {fate.rate_hz != null && <span className="fate-metric">rate={fate.rate_hz.toFixed(1)}</span>}
                              {fate.evidence != null && <span className="fate-metric">evidence={fate.evidence.toFixed(4)}</span>}
                              {fate.hop != null && <span className="fate-metric">hop={fate.hop}</span>}
                            </span>
                          </div>
                        ))}
                        {group.included.length > 15 && (
                          <div className="more-indicator">...and {group.included.length - 15} more included</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      )}

      {/* 1b. Query Expansion */}
      {queryExpansion && queryExpansion.vocabulary_used && (
        <div className="insight-section query-expansion-section">
          <h4 className="insight-section-title">
            Query Expansion
            <span className="badge badge-seed" style={{ marginLeft: 8 }}>Vocabulary</span>
          </h4>
          <div className="query-expansion-compare">
            <div className="query-expansion-col">
              <div className="query-expansion-label">Original Query</div>
              <div className="query-expansion-text query-expansion-text--original">
                {queryExpansion.original_query}
              </div>
            </div>
            <div className="query-expansion-arrow">&#8594;</div>
            <div className="query-expansion-col">
              <div className="query-expansion-label">Expanded Query</div>
              <div className="query-expansion-text query-expansion-text--expanded">
                {queryExpansion.expanded_query}
              </div>
            </div>
          </div>
          {queryExpansion.expansion_notes && (
            <div className="query-expansion-notes">
              {queryExpansion.expansion_notes}
            </div>
          )}
        </div>
      )}

      {/* 1b-SEM. Spiking Episodic Memory (context activation) */}
      {sem && (
        <div className="insight-section sem-section">
          <h4 className="insight-section-title">
            SEM &middot; Episodic Memory Context
            <span
              className="badge badge-seed"
              style={{ marginLeft: 8, textTransform: 'capitalize' }}
              title={sem.mode === 'steer'
                ? 'SEM actively steered this run (rate boosts applied)'
                : 'SEM observed this run without steering'}
            >
              {sem.mode || 'shadow'}
            </span>
            <span className="fate-summary-badge" style={{ marginLeft: 8 }}>
              {(sem.subject_count ?? (sem.subjects || []).length)} subjects, {(sem.directive_count ?? (sem.directives || []).length)} focus
            </span>
            <button className="btn-link btn-inline-toggle" onClick={() => setShowSem(!showSem)}>
              {showSem ? 'Hide' : 'Show'}
            </button>
          </h4>
          <p className="section-hint">
            Emergent subjects derived from this activation and the prioritized focus areas (dynamic goals)
            that {sem.mode === 'steer' ? 'steered' : 'were resolved for'} the run.
          </p>

          {showSem && (
            <div className="sem-body">
              {/* Resolved focus directives */}
              {(sem.directives || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className="query-expansion-label" style={{ marginBottom: 6 }}>Prioritized Focus Areas</div>
                  <table className="insight-table">
                    <thead>
                      <tr>
                        <th>Focus</th>
                        <th>Source</th>
                        <th style={{ textAlign: 'right' }}>Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sem.directives.map((d, i) => (
                        <tr key={`sem-dir-${i}`}>
                          <td>
                            <strong>{d.label || (d.terms || []).join(', ')}</strong>
                            {d.label && (d.terms || []).length > 0 && (
                              <div className="section-hint" style={{ margin: 0 }}>
                                {(d.terms || []).join(', ')}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge badge-seed">
                              {String(d.source || '').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {typeof d.priority === 'number' ? d.priority.toFixed(3) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Emergent subjects */}
              {(sem.subjects || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className="query-expansion-label" style={{ marginBottom: 6 }}>Emergent Subjects</div>
                  <table className="insight-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Concepts</th>
                        <th>Key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sem.subjects.map((s, i) => (
                        <tr key={`sem-subj-${i}`}>
                          <td><strong>{s.label || '(unlabeled)'}</strong></td>
                          <td>{(s.terms || []).join(', ')}</td>
                          <td><code>{String(s.subject_key || '').slice(0, 10)}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Steer effects (only present when SEM_STEER is on) */}
              {sem.focus_boosts && Object.keys(sem.focus_boosts).length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div className="query-expansion-label" style={{ marginBottom: 6 }}>
                    Rate-Coded Boosts ({Object.keys(sem.focus_boosts).length} node{Object.keys(sem.focus_boosts).length === 1 ? '' : 's'})
                  </div>
                  <table className="insight-table">
                    <thead>
                      <tr>
                        <th>Boosted Node</th>
                        <th style={{ textAlign: 'right' }}>Rate (Hz)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(sem.focus_boosts).map(([nid, b]) => (
                        <tr key={`sem-boost-${nid}`}>
                          <td>{(b && b.label) || nid}</td>
                          <td style={{ textAlign: 'right' }}>{(b && b.hz) ?? b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {typeof sem.off_focus_count === 'number' && sem.off_focus_count > 0 && (
                <p className="section-hint">
                  Off-focus inhibition applied to {sem.off_focus_count} node{sem.off_focus_count === 1 ? '' : 's'}
                  {typeof sem.off_focus_penalty === 'number' ? ` (penalty ${sem.off_focus_penalty})` : ''}.
                </p>
              )}

              {(sem.directives || []).length === 0 && (sem.subjects || []).length === 0 && (
                <p className="section-hint">No emergent subjects or focus areas were resolved for this run.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 1c. MLCF Seed Filtering */}
      {mlcfTrace && mlcfTrace.total_kills > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            MLCF Seed Filtering
            <span className="badge badge-seed" style={{ marginLeft: 8 }}>
              {mlcfTrace.seeds_before} &rarr; {mlcfTrace.seeds_after} seeds
            </span>
            <span className="badge" style={{ marginLeft: 4, background: '#e74c3c', color: '#fff' }}>
              {mlcfTrace.total_kills} killed
            </span>
          </h4>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: 8 }}>
            {mlcfTrace.identity_kills > 0 && (
              <div className="metric-pill">
                <span className="metric-label">Identity Mismatch</span>
                <span className="metric-value">{mlcfTrace.identity_kills}</span>
              </div>
            )}
            {mlcfTrace.scope_kills > 0 && (
              <div className="metric-pill">
                <span className="metric-label">Scope Mismatch</span>
                <span className="metric-value">{mlcfTrace.scope_kills}</span>
              </div>
            )}
            {mlcfTrace.constraint_kills > 0 && (
              <div className="metric-pill">
                <span className="metric-label">Constraint Fail</span>
                <span className="metric-value">{mlcfTrace.constraint_kills}</span>
              </div>
            )}
            {mlcfTrace.context_kills > 0 && (
              <div className="metric-pill">
                <span className="metric-label">Context Mismatch</span>
                <span className="metric-value">{mlcfTrace.context_kills}</span>
              </div>
            )}
            {mlcfTrace.structural_kills > 0 && (
              <div className="metric-pill">
                <span className="metric-label">Structural Invalid</span>
                <span className="metric-value">{mlcfTrace.structural_kills}</span>
              </div>
            )}
          </div>

          {/* Query Profile Summary */}
          {mlcfTrace.query_profile && (
            <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '0.85em' }}>
              <strong style={{ fontSize: '0.9em', color: '#4338ca' }}>Query Profile (compared against each node)</strong>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: 4 }}>
                {mlcfTrace.query_profile.identity_entities?.length > 0 && (
                  <span><strong>Entities:</strong> {mlcfTrace.query_profile.identity_entities.join(', ')}</span>
                )}
                {mlcfTrace.query_profile.identity_qualifiers?.length > 0 && (
                  <span><strong>Qualifiers:</strong> {mlcfTrace.query_profile.identity_qualifiers.join(', ')}</span>
                )}
                {mlcfTrace.query_profile.scope_domain && (
                  <span><strong>Domain:</strong> {mlcfTrace.query_profile.scope_domain}</span>
                )}
                {mlcfTrace.query_profile.scope_context && (
                  <span><strong>Context:</strong> {mlcfTrace.query_profile.scope_context}</span>
                )}
                {mlcfTrace.query_profile.scope_applicability?.length > 0 && (
                  <span><strong>Applicability:</strong> {mlcfTrace.query_profile.scope_applicability.join(', ')}</span>
                )}
                {mlcfTrace.query_profile.context_layer && (
                  <span><strong>Context Layer:</strong> {mlcfTrace.query_profile.context_layer}</span>
                )}
              </div>
            </div>
          )}

          {mlcfTrace.killed_nodes && mlcfTrace.killed_nodes.length > 0 && (
            <table className="trace-table trace-table--compact">
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Filter</th>
                  <th>Node Domain</th>
                  <th>Node Context</th>
                  <th>Context Layer</th>
                  <th>Identity</th>
                </tr>
              </thead>
              <tbody>
                {mlcfTrace.killed_nodes.slice(0, 20).map((kn, i) => {
                  const nm = kn.node_mlcf || {};
                  return (
                    <tr key={i}>
                      <td>
                        <button className="entity-link node-link" onClick={() => openNodeModal(kn.node_id)}>
                          {kn.label || kn.node_id}
                        </button>
                      </td>
                      <td><span className="badge">{kn.filter}</span></td>
                      <td style={{ fontSize: '0.85em' }}>{nm.scope_domain || '-'}</td>
                      <td style={{ fontSize: '0.85em' }}>{nm.scope_context || '-'}</td>
                      <td style={{ fontSize: '0.85em' }}>{nm.context_layer || '-'}</td>
                      <td style={{ fontSize: '0.85em' }}>
                        {nm.identity_entity || '-'}
                        {nm.identity_qualifiers?.length > 0 && (
                          <span style={{ color: '#6b7280' }}> ({nm.identity_qualifiers.join(', ')})</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {mlcfTrace.killed_nodes.length > 20 && (
                  <tr>
                    <td colSpan={6} style={{ fontStyle: 'italic', color: '#888' }}>
                      ... and {mlcfTrace.killed_nodes.length - 20} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 1d. CRS Synapse Scoring */}
      {crsTrace && crsTrace.scored_count > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            CRS Connection Scoring
            <span className="badge badge-seed" style={{ marginLeft: 8 }}>
              {crsTrace.scored_count} connections scored
            </span>
            {crsTrace.elapsed_ms != null && (
              <span className="badge" style={{ marginLeft: 4 }}>
                {crsTrace.elapsed_ms}ms
              </span>
            )}
          </h4>

          {/* Signal averages */}
          {crsTrace.signal_averages && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: 10 }}>
              {Object.entries(crsTrace.signal_averages).map(([key, val]) => (
                <div className="metric-pill" key={key}>
                  <span className="metric-label">{key.replace(/_/g, ' ')}</span>
                  <span className="metric-value">{typeof val === 'number' ? val.toFixed(3) : val}</span>
                </div>
              ))}
            </div>
          )}

          {/* Query profile */}
          {crsTrace.query_profile && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: 10 }}>
              <div className="metric-pill">
                <span className="metric-label">Intent</span>
                <span className="metric-value">{crsTrace.query_profile.intent_type}</span>
              </div>
              <div className="metric-pill">
                <span className="metric-label">Authority Bias</span>
                <span className="metric-value">{crsTrace.query_profile.authority_bias}</span>
              </div>
              {crsTrace.query_profile.involves_exceptions && (
                <div className="metric-pill">
                  <span className="metric-label">Exceptions</span>
                  <span className="metric-value">Yes</span>
                </div>
              )}
              {crsTrace.query_profile.scope_tags && crsTrace.query_profile.scope_tags.length > 0 && (
                <div className="metric-pill">
                  <span className="metric-label">Scope</span>
                  <span className="metric-value">{crsTrace.query_profile.scope_tags.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {/* Top boosted synapses */}
          {crsTrace.top_boosted && crsTrace.top_boosted.length > 0 && (
            <>
              <h5 style={{ margin: '8px 0 4px', fontSize: '0.85rem', color: '#666' }}>Top Scored Connections</h5>
              <table className="trace-table trace-table--compact">
                <thead>
                  <tr>
                    <th>Connection</th>
                    <th>Final</th>
                    <th>Semantic</th>
                    <th>TypeCompat</th>
                    <th>Intent</th>
                    <th>Scope</th>
                    <th>Causal</th>
                    <th>Authority</th>
                  </tr>
                </thead>
                <tbody>
                  {crsTrace.top_boosted.slice(0, 10).map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: '0.75rem' }}>{(s.synapse_id || '').substring(0, 8)}...</td>
                      <td><strong>{(s.final_relevance || 0).toFixed(3)}</strong></td>
                      <td>{(s.semantic || 0).toFixed(3)}</td>
                      <td>{(s.node_type_compat || 0).toFixed(3)}</td>
                      <td>{(s.intent_affinity || 0).toFixed(3)}</td>
                      <td>{(s.scope_intersection || 0).toFixed(3)}</td>
                      <td>{(s.causal_modality || 0).toFixed(3)}</td>
                      <td>{(s.authority || 0).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* 2. Rate Coding Derivation */}
      {(rateCoding.length > 0 || rateCodingParams) && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Relevance Scoring Derivation
            {rateCoding.length > 0 && ` (${rateCoding.length} nodes)`}
            <button className="btn-link btn-inline-toggle" onClick={() => setShowRateCoding(!showRateCoding)}>
              {showRateCoding ? 'Hide' : 'Show'}
            </button>
          </h4>

          {/* Global Hybrid Parameters */}
          {rateCodingParams && (
            <div className="rate-coding-params-bar">
              <span className="rc-param">
                <span className="rc-param-label">Semantic W:</span> {rateCodingParams.w_sim}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">BM25 W:</span> {rateCodingParams.w_bm25}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">Softmax T:</span> {rateCodingParams.softmax_temperature}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">Min:</span> {rateCodingParams.min_hz}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">Max:</span> {rateCodingParams.max_hz}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">Decay:</span> {rateCodingParams.contextual_decay_factor}
              </span>
              <span className="rc-param">
                <span className="rc-param-label">Max Hops:</span> {rateCodingParams.contextual_max_hops}
              </span>
            </div>
          )}

          {/* Propagation Stats */}
          {propagationStats && (
            <div className="propagation-stats-bar">
              <span className="prop-stat">
                <strong>{propagationStats.seed_count || 0}</strong> seeds
              </span>
              <span className="prop-stat">
                <strong>{propagationStats.propagated_count || 0}</strong> propagated
              </span>
              <span className="prop-stat">
                <strong>{propagationStats.total_nodes_with_rates || 0}</strong> total
              </span>
            </div>
          )}

          {/* Per-node Rate Coding Table */}
          {showRateCoding && rateCoding.length > 0 && (
            <>
              <table className="insight-table compact">
                <thead>
                  <tr>
                    <th>Node</th>
                    <th>Similarity</th>
                    <th>BM25</th>
                    <th>Hybrid</th>
                    <th>Measure</th>
                    <th>Hop</th>
                    <th>Evidence</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllRateCoding ? rateCoding : rateCoding.slice(0, 20)).map((rc, idx) => (
                    <tr key={idx} className={rc.is_propagated ? 'row-propagated' : 'row-seed'}>
                      <td>
                        <button
                          className="entity-link node-link"
                          onClick={() => openNodeModal(rc.node_id)}
                        >
                          {rc.label || rc.node_id?.substring(0, 12) + '...'}
                        </button>
                      </td>
                      <td className="numeric">{rc.similarity?.toFixed(3)}</td>
                      <td className="numeric">{rc.bm25_raw != null ? rc.bm25_raw.toFixed(3) : '-'}</td>
                      <td className="numeric">{rc.hybrid_score != null ? rc.hybrid_score.toFixed(3) : '-'}</td>
                      <td className="numeric">{rc.rate_hz?.toFixed(1)}</td>
                      <td className="numeric">{rc.hop}</td>
                      <td className="numeric">{rc.evidence != null ? rc.evidence.toFixed(3) : '-'}</td>
                      <td>
                        {rc.is_propagated
                          ? <span className="badge badge-propagated">Propagated</span>
                          : <span className="badge badge-seed">Seed</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rateCoding.length > 20 && (
                <button className="btn-link btn-show-more" onClick={() => setShowAllRateCoding(!showAllRateCoding)}>
                  {showAllRateCoding ? 'Show top 20' : `Show all ${rateCoding.length} nodes`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* 3. Unified Fired Nodes Table */}
      {unifiedNodes.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Selected Nodes ({unifiedNodes.length} total, {unifiedNodes.filter(n => n.in_llm_context).length} in LLM context)
          </h4>
          <div className="unified-nodes-controls">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={filterExcluded}
                onChange={(e) => setFilterExcluded(e.target.checked)}
              />
              Show nodes excluded by potency threshold
            </label>
          </div>
          <table className="insight-table compact unified-nodes-table">
            <thead>
              <tr>
                <th className="expand-cell"></th>
                <th>Node</th>
                <th className="sortable-header" onClick={() => handleSort('potency')}>
                  Potency {renderSortIcon('potency')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('similarity')}>
                  Similarity {renderSortIcon('similarity')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('rate_hz')}>
                  Measure {renderSortIcon('rate_hz')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('spike_count')}>
                  Selections {renderSortIcon('spike_count')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('evidence')}>
                  Evidence {renderSortIcon('evidence')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('hop')}>
                  Hop {renderSortIcon('hop')}
                </th>
                <th>Source</th>
                <th>Tier</th>
                <th>LLM</th>
              </tr>
            </thead>
            <tbody>
              {(showAllUnifiedNodes ? sortedUnifiedNodes : sortedUnifiedNodes.slice(0, 20)).map((node) => {
                const isExpanded = expandedNodeId === node.node_id;
                const tierClass = node.tier === 'EVIDENCE' ? 'row-tier-evidence'
                  : node.tier === 'EXCLUDED' ? 'row-tier-excluded'
                  : node.tier === 'VERBATIM_REFERENCE' ? 'row-tier-verbatim'
                  : '';
                return (
                  <React.Fragment key={node.node_id}>
                    <tr className={tierClass}>
                      <td className="expand-cell">
                        <button
                          className={`row-expand-btn ${isExpanded ? 'open' : ''}`}
                          onClick={() => toggleNodeExpand(node.node_id)}
                          title="Expand details"
                        >
                          &#9654;
                        </button>
                      </td>
                      <td>
                        <button
                          className="entity-link node-link"
                          onClick={() => openNodeModal(node.node_id)}
                          title={`View node ${node.node_id}`}
                        >
                          {node.label}
                        </button>
                        {node.is_outcome && <span className="badge badge-outcome">Outcome</span>}
                      </td>
                      <td className="numeric">
                        {node.potency != null ? (
                          <span className={`potency-value ${node.potency >= 0.3 ? 'potency-high' : node.potency >= 0.15 ? 'potency-mid' : 'potency-low'}`}>
                            {node.potency.toFixed(3)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="numeric">{node.similarity != null ? node.similarity.toFixed(3) : '-'}</td>
                      <td className="numeric">{node.rate_hz != null ? node.rate_hz.toFixed(1) : '-'}</td>
                      <td className="numeric">{node.spike_count}</td>
                      <td className="numeric">{node.evidence != null ? node.evidence.toFixed(3) : '-'}</td>
                      <td className="numeric">{node.hop != null ? node.hop : '-'}</td>
                      <td>
                        {node.is_seed
                          ? <span className="badge badge-seed">Seed</span>
                          : <span className="badge badge-propagated">Propagated</span>
                        }
                      </td>
                      <td>
                        <span className={`badge badge-tier-${(node.tier || 'unknown').toLowerCase()}`}>
                          {node.tier === 'VERBATIM_REFERENCE' ? 'Verbatim' : node.tier || '?'}
                        </span>
                      </td>
                      <td>
                        {node.in_llm_context
                          ? <span className="badge badge-fired">Yes</span>
                          : <span className="badge badge-silent">No</span>
                        }
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="node-expand-row">
                        <td colSpan="11">
                          <div className="node-expand-content">
                            {/* Node Type */}
                            <div className="expand-detail-item">
                              <span className="expand-detail-label">Type:</span>
                              <span>{node.node_type}</span>
                            </div>
                            {/* Neuron Model */}
                            {node.neuron_model && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Processing Model:</span>
                                <span className={`badge badge-model-${node.neuron_model === 'PoissonSource' ? 'poisson' : 'lif'}`}>
                                  {node.neuron_model === 'PoissonSource' ? 'Poisson' : 'Standard'}
                                </span>
                                {node.threshold != null && <span style={{ marginLeft: 8 }}>theta={node.threshold.toFixed(3)}</span>}
                                {node.tau_m != null && <span style={{ marginLeft: 8 }}>tau_m={node.tau_m.toFixed(1)}ms</span>}
                              </div>
                            )}
                            {/* Firing Equation */}
                            {node.firing_equation && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Selection Equation:</span>
                                <code className="expand-detail-code">{node.firing_equation}</code>
                              </div>
                            )}
                            {/* Potency Breakdown */}
                            {node.potency != null && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Potency Breakdown:</span>
                                <span className="potency-breakdown">
                                  {node.norm_rate != null && <span className="pb-chip">rate={node.norm_rate.toFixed(3)}</span>}
                                  {node.norm_evidence != null && <span className="pb-chip">evidence={node.norm_evidence.toFixed(3)}</span>}
                                  {node.norm_spikes != null && <span className="pb-chip">selections={node.norm_spikes.toFixed(3)}</span>}
                                  {node.query_relevance != null && <span className="pb-chip">qrel={node.query_relevance.toFixed(3)}</span>}
                                  {node.prop_score != null && <span className="pb-chip">prop={node.prop_score.toFixed(3)}</span>}
                                  {node.gate_bonus != null && <span className="pb-chip">gate={node.gate_bonus.toFixed(1)}</span>}
                                </span>
                              </div>
                            )}
                            {/* Rate Coding Details */}
                            {(node.bm25_raw != null || node.hybrid_score != null) && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Relevance Scoring:</span>
                                <span>
                                  Similarity={node.similarity?.toFixed(3)},
                                  {node.bm25_raw != null && ` BM25=${node.bm25_raw.toFixed(3)},`}
                                  {node.hybrid_score != null && ` Hybrid=${node.hybrid_score.toFixed(3)},`}
                                  {` Rate=${node.rate_hz?.toFixed(1)}`}
                                </span>
                              </div>
                            )}
                            {/* Gate Contributions */}
                            {node.gate_contributions && node.gate_contributions.length > 0 && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Gate Contributions:</span>
                                <span>{node.gate_contributions.join(', ')}</span>
                              </div>
                            )}
                            {/* Activation Path */}
                            {node.activation_path && node.activation_path.length > 0 && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Activation Path:</span>
                                <span className="activation-path-chain">
                                  {node.activation_path.map((nid, i) => (
                                    <React.Fragment key={i}>
                                      {i > 0 && <span className="path-arrow">&rarr;</span>}
                                      <button className="entity-link node-link" onClick={() => openNodeModal(nid)}>
                                        {nid.substring(0, 8)}
                                      </button>
                                    </React.Fragment>
                                  ))}
                                  <span className="path-arrow">&rarr;</span>
                                  <strong>{node.label}</strong>
                                </span>
                              </div>
                            )}
                            {/* Contributing Synapses */}
                            {node.contributing_synapses && node.contributing_synapses.length > 0 && (
                              <div className="expand-detail-item">
                                <span className="expand-detail-label">Contributing Connections:</span>
                                <div className="contributing-synapses">
                                  {node.contributing_synapses.map((cs, i) => (
                                    <span key={i} className="contrib-synapse-pill">
                                      <button className="entity-link synapse-link" onClick={() => openSynapseModal(cs.synapse_id)}>
                                        {cs.synapse_id?.substring(0, 8)}
                                      </button>
                                      {cs.relevance != null && <span className="contrib-val">rel={cs.relevance.toFixed(2)}</span>}
                                      {cs.confidence != null && <span className="contrib-val">conf={cs.confidence.toFixed(2)}</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {sortedUnifiedNodes.length > 20 && (
            <button className="btn-link btn-show-more" onClick={() => setShowAllUnifiedNodes(!showAllUnifiedNodes)}>
              {showAllUnifiedNodes ? 'Show top 20' : `Show all ${sortedUnifiedNodes.length} nodes`}
            </button>
          )}
        </div>
      )}

      {/* 4. Nodes Below Potency Threshold */}
      {excludedNodes.length > 0 && (
        <div className="insight-section excluded-nodes-section">
          <h4 className="insight-section-title">
            Nodes Below Potency Threshold ({excludedNodes.length})
            <button className="btn-link btn-inline-toggle" onClick={() => setShowExcludedNodes(!showExcludedNodes)}>
              {showExcludedNodes ? 'Hide' : 'Show Analysis'}
            </button>
          </h4>
          <p className="section-hint">
            These nodes selected during Platform simulation but were excluded from the LLM context because their potency score fell below the threshold. Review the suggestions to improve their ranking.
          </p>
          {showExcludedNodes && (
            <div className="excluded-nodes-list">
              {excludedNodes.slice(0, 15).map((node) => {
                const suggestions = getExcludedSuggestion(node);
                return (
                  <div key={node.node_id} className="excluded-node-card">
                    <div className="excluded-node-header">
                      <button
                        className="entity-link node-link"
                        onClick={() => openNodeModal(node.node_id)}
                      >
                        {node.label}
                      </button>
                      <span className="excluded-potency">
                        Potency: <strong>{node.potency?.toFixed(4) || '0.0000'}</strong>
                      </span>
                    </div>
                    <div className="excluded-node-scores">
                      <span className="en-score">sim={node.similarity?.toFixed(3) || '0'}</span>
                      <span className="en-score">rate={node.rate_hz?.toFixed(1) || '0'}</span>
                      <span className="en-score">selections={node.spike_count}</span>
                      <span className="en-score">evidence={node.evidence?.toFixed(3) || '0'}</span>
                      <span className="en-score">hop={node.hop ?? '?'}</span>
                      <span className="en-score">gate={node.gate_bonus?.toFixed(1) || '0'}</span>
                    </div>
                    <div className="excluded-suggestions">
                      {suggestions.map((s, i) => (
                        <div key={i} className="suggestion-item">
                          <span className="suggestion-component">{s.component} ({s.value}):</span>
                          <span className="suggestion-action">{s.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {excludedNodes.length > 15 && (
                <div className="more-indicator">...and {excludedNodes.length - 15} more excluded nodes</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 5. Explanation Chain / Propagation Visual */}
      {explanationChain.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">Propagation Chain</h4>
          {trace.decision_label && (
            <div className="chain-decision">
              Decision: <strong>{trace.decision_label}</strong>
            </div>
          )}
          <div className="explanation-path-visual">
            {explanationChain.map((hop, idx) => {
              // Look up synapse details between this hop and the next
              const nextHop = explanationChain[idx + 1];
              const synapseKey = nextHop ? `${hop.source_node_id}->${nextHop.source_node_id}` : null;
              const synDetail = synapseKey ? synapseBySourceTarget[synapseKey] : null;

              // Look up rate coding for this node
              const hopPotency = potencyScores[hop.source_node_id];

              return (
                <div key={idx} className="path-hop-item">
                  <div className="hop-node-box">
                    <button
                      className="entity-link node-link"
                      onClick={() => openNodeModal(hop.source_node_id)}
                    >
                      {hop.source_label}
                    </button>
                    <div className="hop-stats-row">
                      <span>{hop.spike_count} selections</span>
                      <span>contrib: {hop.contribution?.toFixed(2)}</span>
                    </div>
                    {hopPotency && (
                      <div className="hop-stats-row">
                        <span>rate: {hopPotency.rate_hz?.toFixed(1)}</span>
                        <span>potency: {hopPotency.potency?.toFixed(3)}</span>
                      </div>
                    )}
                  </div>
                  {idx < explanationChain.length - 1 && (
                    <div className="hop-arrow-box">
                      <span className="hop-arrow-symbol">&rarr;</span>
                      <span className="hop-relation">{hop.relation}</span>
                      <span className="hop-weight">w={hop.weight?.toFixed(2)}</span>
                      {synDetail && synDetail.relevance != null && (
                        <span className="hop-relevance">rel={synDetail.relevance.toFixed(2)}</span>
                      )}
                      {synDetail && synDetail.confidence != null && (
                        <span className="hop-confidence">conf={synDetail.confidence.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {trace.decision_label && (
              <div className="path-hop-item">
                <div className="hop-node-box hop-decision">
                  <button
                    className="entity-link node-link"
                    onClick={() => openNodeModal(trace.decision_node_id)}
                  >
                    {trace.decision_label}
                  </button>
                  <div className="hop-stats-row">Decision Node</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6. Chain Analysis (from executor) */}
      {chainAnalysis && (
        <div className="insight-section">
          <h4 className="insight-section-title">Chain Analysis</h4>
          <div className="chain-analysis-bar">
            <span className="ca-item"><strong>{chainAnalysis.total_nodes_fired || 0}</strong> total selected</span>
            <span className="ca-item"><strong>{chainAnalysis.seed_nodes_fired || 0}</strong> seeds selected</span>
            <span className="ca-item"><strong>{chainAnalysis.propagated_nodes_fired || 0}</strong> propagated selected</span>
            {(chainAnalysis.other_nodes_fired || 0) > 0 && (
              <span className="ca-item"><strong>{chainAnalysis.other_nodes_fired}</strong> other selected</span>
            )}
            <span className="ca-item"><strong>{chainAnalysis.gates_satisfied || 0}</strong>/{chainAnalysis.gates_total || 0} gates</span>
            <span className="ca-item">peak selection tick: <strong>T{chainAnalysis.peak_firing_tick || 0}</strong></span>
          </div>
          {chainAnalysis.firing_by_hop && Object.keys(chainAnalysis.firing_by_hop).length > 0 && (
            <div className="firing-by-hop">
              <span className="fbh-label">Selection by hop:</span>
              {Object.entries(chainAnalysis.firing_by_hop).sort(([a], [b]) => Number(a) - Number(b)).map(([hop, count]) => (
                <span key={hop} className="fbh-item">
                  {Number(hop) < 0 ? 'Other' : `Hop ${hop}`}: <strong>{count}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6a. Synapse Gating Stats */}
      {trace.synapse_gating && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Connection Gating
            <span className="badge badge-seed" style={{ marginLeft: 8 }}>
              {trace.synapse_gating.active_synapses}/{trace.synapse_gating.total_synapses} active
            </span>
          </h4>
          <div className="chain-analysis-bar">
            <span className="ca-item">
              <strong>{trace.synapse_gating.gated_count || 0}</strong> gated (zeroed)
            </span>
            <span className="ca-item">
              <strong>{trace.synapse_gating.modulated_count || 0}</strong> scored &amp; kept
            </span>
            <span className="ca-item">
              <strong>{trace.synapse_gating.unscored_count || 0}</strong> unscored (x{trace.synapse_gating.unscored_penalty ?? 0.15})
            </span>
            <span className="ca-item">
              <strong>{trace.synapse_gating.verbatim_kept_count || 0}</strong> verbatim kept
            </span>
            <span className="ca-item">
              threshold: <strong>{trace.synapse_gating.relevance_gate_threshold ?? 0.25}</strong>
            </span>
          </div>
          {trace.synapse_gating.total_synapses > 0 && (
            <div style={{ marginTop: 6, fontSize: '0.85em', color: '#666' }}>
              {((trace.synapse_gating.gated_count / trace.synapse_gating.total_synapses) * 100).toFixed(1)}% of connections gated (disconnected),{' '}
              {((trace.synapse_gating.active_synapses / trace.synapse_gating.total_synapses) * 100).toFixed(1)}% remain active
            </div>
          )}
        </div>
      )}

      {/* 6b. Prioritized Context Bundle (from prompt_expansions) */}
      {promptExpansions.prioritized_evidence_bundle && (
        <div className="insight-section">
          <h4 className="insight-section-title">Prioritized Context Bundle (Sent to LLM)</h4>
          <p className="section-hint" style={{ fontSize: '0.85em', color: '#666', margin: '4px 0 8px' }}>
            This evidence bundle was sent to the LLM to reduce hallucination. Nodes are ranked by Platform selection potency (highest first). Only nodes above the potency threshold are included.
          </p>
          <TextWithTables
            text={promptExpansions.prioritized_evidence_bundle}
            preformatted
          />
        </div>
      )}

      {/* 6c. Platform Context Assembly (Technical Payload) */}
      {contextAssembly && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Platform Context Assembly
            <span style={{ fontWeight: 400, fontSize: '0.85em', marginLeft: 8, color: '#666' }}>
              {(contextAssembly.evidence_nodes || []).length} evidence,{' '}
              {(contextAssembly.verbatim_nodes || []).length} verbatim,{' '}
              {(contextAssembly.excluded_nodes || []).length} excluded
            </span>
            <button className="btn-link btn-inline-toggle" onClick={() => setShowContextAssembly(!showContextAssembly)}>
              {showContextAssembly ? 'Hide' : 'Show'}
            </button>
          </h4>

          {/* Summary bar */}
          <div className="context-assembly-summary">
            <span className="ca-stat">
              <span className="ca-stat-label">Total Selected:</span> <strong>{contextAssembly.total_fired || 0}</strong>
            </span>
            <span className="ca-stat">
              <span className="ca-stat-label">Potency Threshold:</span> <strong>{contextAssembly.potency_threshold?.toFixed(2) ?? '0.10'}</strong>
              {(() => {
                const pd = contextAssembly.potency_decision;
                if (!pd || typeof pd !== 'object') return null;
                const mode = pd.mode || 'manual';
                const escCount = Array.isArray(pd.escalations) ? pd.escalations.length : 0;
                return (
                  <span
                    className={`badge badge-${mode === 'auto' ? 'evidence' : 'verbatim_reference'}`}
                    style={{ marginLeft: 6, fontSize: '0.78em' }}
                    title={
                      mode === 'auto'
                        ? `Auto: knee=${pd.knee?.toFixed?.(2) ?? '-'}, p65=${pd.p65?.toFixed?.(2) ?? '-'}, topK=${pd.topk_floor?.toFixed?.(2) ?? '-'}, candidates=${pd.candidate_count ?? '-'}, escalations=${escCount}`
                        : 'Manual (static value)'
                    }
                  >
                    {mode === 'auto' ? `AUTO${escCount ? ` +${escCount}` : ''}` : 'MANUAL'}
                  </span>
                );
              })()}
            </span>
            <span className="ca-stat">
              <span className="ca-stat-label">Bundle Size:</span> <strong>{contextAssembly.bundle_char_count?.toLocaleString() || 0} chars</strong>
            </span>
          </div>

          {showContextAssembly && (
            <div className="context-assembly-detail">
              {/* Evidence Nodes Table */}
              {(contextAssembly.evidence_nodes || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h5 style={{ margin: '8px 0', fontSize: '0.9em', color: '#374151' }}>
                    Evidence Nodes ({contextAssembly.evidence_nodes.length})
                  </h5>
                  <table className="insight-table compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Label</th>
                        <th>Potency</th>
                        <th>Tier</th>
                        <th>Measure</th>
                        <th>Query Rel.</th>
                        <th>Selections</th>
                        <th>Hop</th>
                        <th>Evidence</th>
                        <th>In Trail</th>
                        <th>Breakdown (R/E/S/Q/P/G)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contextAssembly.evidence_nodes.map((node, idx) => {
                        const potencyColor = (node.potency ?? 0) > 0.5
                          ? '#16a34a' : (node.potency ?? 0) > 0.2
                          ? '#ca8a04' : '#dc2626';
                        return (
                          <tr key={node.node_id || idx}>
                            <td className="numeric">{idx + 1}</td>
                            <td>
                              <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                                {node.label || node.node_id?.substring(0, 16) + '...'}
                              </button>
                            </td>
                            <td className="numeric" style={{ color: potencyColor, fontWeight: 600 }}>
                              {node.potency?.toFixed(4) ?? '-'}
                            </td>
                            <td>
                              <span className={`badge badge-${(node.tier || 'evidence').toLowerCase()}`}>
                                {node.tier || 'EVIDENCE'}
                              </span>
                            </td>
                            <td className="numeric">{node.rate_hz?.toFixed(1) ?? '-'}</td>
                            <td className="numeric">{node.query_relevance?.toFixed(3) ?? '-'}</td>
                            <td className="numeric">{node.spike_count ?? '-'}</td>
                            <td className="numeric">{node.hop ?? '-'}</td>
                            <td className="numeric">{node.evidence?.toFixed(3) ?? '-'}</td>
                            <td style={{ textAlign: 'center' }}>{node.in_trail ? '\u2713' : ''}</td>
                            <td className="numeric" style={{ fontSize: '0.8em', color: '#6b7280' }}>
                              {[
                                node.norm_rate?.toFixed(2),
                                node.norm_evidence?.toFixed(2),
                                node.norm_spikes?.toFixed(2),
                                node.query_relevance?.toFixed(2),
                                node.prop_score?.toFixed(2),
                                node.gate_bonus?.toFixed(1),
                              ].join(' / ')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Verbatim Reference Nodes Table */}
              {(contextAssembly.verbatim_nodes || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h5 style={{ margin: '8px 0', fontSize: '0.9em', color: '#374151' }}>
                    Verbatim References ({contextAssembly.verbatim_nodes.length})
                  </h5>
                  <table className="insight-table compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Label</th>
                        <th>Potency / VC Score</th>
                        <th>Tier</th>
                        <th>Query Rel.</th>
                        <th>Source Channel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contextAssembly.verbatim_nodes.map((node, idx) => (
                        <tr key={node.node_id || idx}>
                          <td className="numeric">{idx + 1}</td>
                          <td>
                            <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                              {node.label || node.node_id?.substring(0, 16) + '...'}
                            </button>
                          </td>
                          <td className="numeric">
                            {node.potency?.toFixed(4) ?? node._vc_score?.toFixed(4) ?? '-'}
                          </td>
                          <td>
                            <span className="badge badge-verbatim_reference">
                              {node.tier || 'VERBATIM_REFERENCE'}
                            </span>
                          </td>
                          <td className="numeric">{node.query_relevance?.toFixed(3) ?? '-'}</td>
                          <td>
                            <span className={`badge badge-source-${node.source || 'snn'}`}>
                              {node.source === 'channel2' ? 'Channel 2 (Content Search)'
                                : node.source === 'channel1' ? 'Channel 1 (Child Selection)'
                                : 'Platform Selection'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Top Excluded Nodes (collapsible) */}
              {(contextAssembly.excluded_nodes || []).length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <h5 style={{ margin: '8px 0', fontSize: '0.9em', color: '#374151', cursor: 'pointer' }}
                      onClick={() => setShowAssemblyExcluded(!showAssemblyExcluded)}>
                    Excluded Nodes ({contextAssembly.excluded_nodes.length})
                    <span style={{ marginLeft: 6, fontSize: '0.85em', color: '#9ca3af' }}>
                      {showAssemblyExcluded ? '\u25BC' : '\u25B6'} top 10 by potency
                    </span>
                  </h5>
                  {showAssemblyExcluded && (
                    <table className="insight-table compact">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Label</th>
                          <th>Potency</th>
                          <th>Measure</th>
                          <th>Query Rel.</th>
                          <th>Selections</th>
                          <th>Hop</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contextAssembly.excluded_nodes.slice(0, 10).map((node, idx) => (
                          <tr key={node.node_id || idx} style={{ opacity: 0.7 }}>
                            <td className="numeric">{idx + 1}</td>
                            <td>
                              <button className="entity-link node-link" onClick={() => openNodeModal(node.node_id)}>
                                {node.label || node.node_id?.substring(0, 16) + '...'}
                              </button>
                            </td>
                            <td className="numeric" style={{ color: '#dc2626' }}>
                              {node.potency?.toFixed(4) ?? '-'}
                            </td>
                            <td className="numeric">{node.rate_hz?.toFixed(1) ?? '-'}</td>
                            <td className="numeric">{node.query_relevance?.toFixed(3) ?? '-'}</td>
                            <td className="numeric">{node.spike_count ?? '-'}</td>
                            <td className="numeric">{node.hop ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 7. LLM Prompt Section */}
      {(promptExpansions.full_llm_prompt || promptExpansions.system_message || promptExpansions.snn_decision_summary) && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            LLM Prompt
            <button className="btn-link btn-inline-toggle" onClick={() => setShowLlmPrompt(!showLlmPrompt)}>
              {showLlmPrompt ? 'Hide' : 'Show'}
            </button>
          </h4>
          {showLlmPrompt && (
            <div className="llm-prompt-content">
              {/* System Message */}
              {promptExpansions.system_message && (
                <div className="llm-prompt-subsection">
                  <h5 className="llm-prompt-subtitle">System Message</h5>
                  <pre className="llm-prompt-pre">{promptExpansions.system_message}</pre>
                </div>
              )}
              {/* Platform Decision Summary */}
              {promptExpansions.snn_decision_summary && (
                <div className="llm-prompt-subsection">
                  <h5 className="llm-prompt-subtitle">Platform Decision Summary</h5>
                  <pre className="llm-prompt-pre">{promptExpansions.snn_decision_summary}</pre>
                </div>
              )}
              {/* Node Diagnostics */}
              {promptExpansions.node_diagnostics && (
                <div className="llm-prompt-subsection">
                  <h5 className="llm-prompt-subtitle">Node Diagnostics</h5>
                  <pre className="llm-prompt-pre">{promptExpansions.node_diagnostics}</pre>
                </div>
              )}
              {/* Full LLM Prompt */}
              {promptExpansions.full_llm_prompt && (
                <div className="llm-prompt-subsection">
                  <h5 className="llm-prompt-subtitle">Full LLM Prompt</h5>
                  <pre className="llm-prompt-pre">{promptExpansions.full_llm_prompt}</pre>
                </div>
              )}
              {/* LLM Model */}
              {promptExpansions.llm_model && (
                <div className="llm-prompt-meta">
                  Model: <strong>{promptExpansions.llm_model}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 7b. Judge LLM Pass (Phase 9) */}
      {(promptExpansions.judge_refinement_notes || promptExpansions.judge_prompt) && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Judge LLM Pass (Phase 9)
            {promptExpansions.judge_prompt && (
              <button className="btn-link btn-inline-toggle" onClick={() => setShowJudgePrompt(!showJudgePrompt)}>
                {showJudgePrompt ? 'Hide Prompt' : 'Show Prompt'}
              </button>
            )}
          </h4>
          {promptExpansions.judge_refinement_notes && (
            <div className="judge-verdict" style={{
              padding: '8px 12px',
              borderRadius: '6px',
              background: promptExpansions.judge_refinement_notes.includes('refined') ? '#fef3c7' : '#ecfdf5',
              border: `1px solid ${promptExpansions.judge_refinement_notes.includes('refined') ? '#f59e0b' : '#10b981'}`,
              marginBottom: '8px',
              fontSize: '0.9em',
            }}>
              <strong>Verdict:</strong> {promptExpansions.judge_refinement_notes}
            </div>
          )}
          {showJudgePrompt && promptExpansions.judge_prompt && (
            <pre className="llm-prompt-pre">{promptExpansions.judge_prompt}</pre>
          )}
        </div>
      )}

      {/* 8. Strongest Neurons */}
      {strongestByRate.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">Strongest Nodes (by Peak Measure)</h4>
          <div className="strongest-list">
            {strongestByRate.map((act, idx) => (
              <div key={idx} className="strongest-item">
                <span className="strongest-rank">#{idx + 1}</span>
                <button
                  className="entity-link node-link"
                  onClick={() => openNodeModal(act.node_id)}
                >
                  {act.label || act.node_id?.substring(0, 12)}
                </button>
                <span className="strongest-rate">{act.peak_rate_hz?.toFixed(1)}</span>
                <span className="strongest-spikes">{act.spike_count} selections</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 9. Synapse Relationships Activated (enriched) */}
      {synapses.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">Connection Relationships Selected ({synapses.length} total)</h4>
          <div className="relation-summary">
            {sortedRelations.map(([rel, count]) => (
              <span key={rel} className="relation-pill">
                {rel} <strong>({count})</strong>
              </span>
            ))}
          </div>
          <button
            className="btn-link btn-show-more"
            onClick={() => setShowSynapseTable(!showSynapseTable)}
          >
            {showSynapseTable ? 'Hide connection details' : 'Show all connections'}
          </button>
          {showSynapseTable && (
            <table className="insight-table compact">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Relation</th>
                  <th>Target</th>
                  <th>Weight</th>
                  <th>Relevance</th>
                  <th>Confidence</th>
                  <th>Strength</th>
                  <th>Mod. Weight</th>
                  <th title="Semantic similarity between user query and connection context (0-1)">Sem. Sim</th>
                  <th title="BM25/keyword overlap between query tokens and connection text (0-1)">Keyword</th>
                </tr>
              </thead>
              <tbody>
                {synapses.slice(0, 100).map((syn, idx) => {
                  const semSimColor = syn.semantic_similarity != null
                    ? syn.semantic_similarity > 0.5 ? '#16a34a' : syn.semantic_similarity > 0.2 ? '#ca8a04' : '#9ca3af'
                    : undefined;
                  const kwColor = syn.keyword_overlap != null
                    ? syn.keyword_overlap > 0.5 ? '#16a34a' : syn.keyword_overlap > 0.2 ? '#ca8a04' : '#9ca3af'
                    : undefined;
                  return (
                  <tr key={idx}>
                    <td>
                      <button className="entity-link node-link" onClick={() => openNodeModal(syn.source_id)}>
                        {syn.source_label || syn.source_id?.substring(0, 12)}
                      </button>
                    </td>
                    <td className="relation-cell">
                      <button className="entity-link synapse-link" onClick={() => openSynapseModal(syn.synapse_id)}>
                        {syn.relation || syn.edge_name || '-'}
                      </button>
                    </td>
                    <td>
                      <button className="entity-link node-link" onClick={() => openNodeModal(syn.target_id)}>
                        {syn.target_label || syn.target_id?.substring(0, 12)}
                      </button>
                    </td>
                    <td className="numeric">{syn.weight?.toFixed(2)}</td>
                    <td className="numeric">{syn.relevance != null ? syn.relevance.toFixed(3) : '-'}</td>
                    <td className="numeric">{syn.confidence != null ? syn.confidence.toFixed(3) : '-'}</td>
                    <td>
                      {syn.relation_strength
                        ? <span className={`badge badge-strength-${syn.relation_strength}`}>{syn.relation_strength}</span>
                        : '-'
                      }
                    </td>
                    <td className="numeric">{syn.modulated_weight != null ? syn.modulated_weight.toFixed(3) : '-'}</td>
                    <td className="numeric" style={semSimColor ? { color: semSimColor, fontWeight: 600 } : undefined}>
                      {syn.semantic_similarity != null ? syn.semantic_similarity.toFixed(3) : '-'}
                    </td>
                    <td className="numeric" style={kwColor ? { color: kwColor, fontWeight: 600 } : undefined}>
                      {syn.keyword_overlap != null ? syn.keyword_overlap.toFixed(3) : '-'}
                    </td>
                  </tr>
                  );
                })}
                {synapses.length > 100 && (
                  <tr><td colSpan="10" className="more-indicator">...and {synapses.length - 100} more</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 10. Gate Evaluation */}
      {gates.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">Gate Evaluation ({gates.length} gates)</h4>
          <table className="insight-table compact">
            <thead>
              <tr>
                <th>Gate</th>
                <th>Type</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Inputs</th>
                <th>Selected Inputs</th>
              </tr>
            </thead>
            <tbody>
              {gates.map((gate, idx) => (
                <tr key={idx} className={gate.fired ? 'row-gate-fired' : ''}>
                  <td>
                    <button className="entity-link node-link" onClick={() => openNodeModal(gate.gate_id)}>
                      {gate.gate_id?.substring(0, 12)}...
                    </button>
                  </td>
                  <td>
                    <span className={`badge badge-gate-${(gate.gate_type || 'AND').toLowerCase()}`}>
                      {gate.gate_type || 'AND'}
                    </span>
                  </td>
                  <td>
                    {gate.fired
                      ? <span className="badge badge-fired">Satisfied</span>
                      : <span className="badge badge-silent">Pending</span>
                    }
                  </td>
                  <td className="numeric">{gate.satisfaction_progress || '-'}</td>
                  <td className="numeric">
                    {(gate.required_inputs || gate.inputs || []).length}
                  </td>
                  <td>
                    {(gate.fired_inputs || []).length > 0 ? (
                      <span className="gate-fired-inputs">
                        {gate.fired_inputs.slice(0, 3).map((nid, i) => (
                          <button key={i} className="entity-link node-link" onClick={() => openNodeModal(nid)}>
                            {nid.substring(0, 8)}
                          </button>
                        ))}
                        {gate.fired_inputs.length > 3 && <span className="tick-more">+{gate.fired_inputs.length - 3}</span>}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 11. Fired Per Tick Timeline */}
      {firedPerTick.length > 0 && (
        <div className="insight-section">
          <h4 className="insight-section-title">
            Selected Per Tick ({firedPerTick.length} ticks)
            <button className="btn-link btn-inline-toggle" onClick={() => setShowFiredPerTick(!showFiredPerTick)}>
              {showFiredPerTick ? 'Hide' : 'Show'}
            </button>
          </h4>
          {showFiredPerTick && (
            <div className="fired-per-tick">
              {firedPerTick.map((tick, idx) => {
                const nodeIds = Array.isArray(tick) ? tick : (tick.fired_node_ids || tick.node_ids || []);
                return (
                  <div key={idx} className="tick-row">
                    <span className="tick-label">T{idx + 1}</span>
                    <span className="tick-count">{nodeIds.length} selected</span>
                    <span className="tick-nodes">
                      {nodeIds.slice(0, 5).map((nid, i) => (
                        <button key={i} className="entity-link node-link tick-node-link" onClick={() => openNodeModal(nid)}>
                          {nid?.substring(0, 8)}
                        </button>
                      ))}
                      {nodeIds.length > 5 && <span className="tick-more">+{nodeIds.length - 5} more</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 12. Full Answer */}
      {insight.natural_language_answer && (
        <div className="insight-section">
          <h4 className="insight-section-title">Full Answer</h4>
          <TextWithTables text={insight.natural_language_answer} className="insight-answer" />
        </div>
      )}

      {/* Node Entity Modal */}
      <NodeEntityModal
        isOpen={nodeModalOpen}
        node={selectedNodeId ? { id: selectedNodeId } : null}
        onClose={() => { setNodeModalOpen(false); setSelectedNodeId(null); }}
        onEntityUpdated={() => {}}
        graphId={graphId}
        mlcfMetadata={selectedNodeId ? nodeMlcfMetadata[selectedNodeId] || null : null}
      />

      {/* Synapse Entity Modal */}
      <SynapseEntityModal
        isOpen={synapseModalOpen}
        synapse={selectedSynapseId ? { id: selectedSynapseId, synapseid: selectedSynapseId } : null}
        onClose={() => { setSynapseModalOpen(false); setSelectedSynapseId(null); }}
        onEntityUpdated={() => {}}
        graphId={graphId}
      />
    </div>
  );
}

export default ExecutionInsightDetail;
